import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { executeQuery, getMembers } from "../../cube/api";
import { getSoldePeriodes, getArticlesByPeriode } from "../../solde/api";
import { CUBE_NAME, fetchMeasures } from "../../cube/cubeSlice";
import { useAppDispatch, useAppSelector } from "../../../shared/store/hooks";
import { colHeaderShouldNegate, applyRule } from "../../cube/measureRules";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { PeriodDashboard } from "../../cube/components/PeriodDashboard";
import ComparisonView from "../../cube/components/ComparisonView";
import EvolutionView from "../../cube/components/EvolutionView";
import SaveQueryModal from "../../saved-queries/components/SaveQueryModal";
import SavedQueriesDropdown from "../../saved-queries/components/SavedQueriesDropdown";
import { useExportCouleurPDF } from "../export/useExportCouleurPDF";
import { useExportCouleurExcel } from "../export/useExportCouleurExcel";
import { useExportDrilldownPDF } from "../export/useExportDrilldownPDF";
import { useUserScope } from "../../auth/useUserScope";
import { applyDateMask, dayBefore, displayToIso, fixYear, isValidDate, isoToDisplay } from "../../../shared/lib/date";
import { fmtNum } from "../../../shared/lib/format";
import { inputStyle, tdS, thS } from "../../../shared/ui/styles";
import { FONTS, NW } from "../../../shared/ui/tokens";
import { CUBE_DIMENSIONS } from "../../cube/dimensions";

// ─────────────────────────────────────────────────────────────────────────────
// Constants — SousFamille dimension
// ─────────────────────────────────────────────────────────────────────────────
// Noms du cube : voir features/cube/dimensions.ts
const { dimensionName: DIM_NAME, hierarchyName: HIER_NAME, levelName: LEVEL_NAME } =
  CUBE_DIMENSIONS.sousfamille;
const TOP_OPTIONS = [5, 10, 20, 50];

// ── Nawara brand palette ───────────────────────────────────────────────────────

// Chip palette for member pills
const CHIP_PALETTE = [
  "#4338CA",
  "#3ECFBF",
  "#0D9488",
  "#D97706",
  "#2B5CE6",
  "#EA580C",
  "#2B5CE6",
  "#0D8A80",
  "#047857",
  "#B45309",
  "#1E40AF",
  "#C2410C",
  "#1D4ED8",
  "#0E7490",
  "#065F46",
  "#92400E",
];

// ── View modes ────────────────────────────────────────────────────────────────
type ViewMode = "tableau" | "dashboard" | "comparaison" | "evolution";

const VIEW_MODES: { mode: ViewMode; icon: string; label: string }[] = [
  { mode: "tableau", icon: "📋", label: "Tableau" },
  { mode: "dashboard", icon: "📊", label: "Dashboard" },
  { mode: "comparaison", icon: "⚖️", label: "Comparaison" },
  { mode: "evolution", icon: "📈", label: "Évolution" },
];

// ─────────────────────────────────────────────────────────────────────────────
// Style helpers
// ─────────────────────────────────────────────────────────────────────────────
const card = (extra?: React.CSSProperties): React.CSSProperties => ({
  background: NW.white,
  borderRadius: 14,
  border: `1.5px solid ${NW.gray200}`,
  boxShadow: "0 2px 16px rgba(29,91,168,0.07)",
  ...extra,
});

// ─────────────────────────────────────────────────────────────────────────────
// SoldePeriode interface
// ─────────────────────────────────────────────────────────────────────────────
interface SoldePeriode {
  code_periode: string;
  date_debut: string | null;
  date_fin: string | null;
  date_deb_priv: string | null;
  date_debut2: string | null;
  date_debut3: string | null;
  date_debut4: string | null;
  cod_dep: string | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Démarque helpers
// ─────────────────────────────────────────────────────────────────────────────
interface DemarquePhase {
  label: string;
  icon: string;
  dateFrom: string;
  dateTo: string;
}

function buildDemarquePhases(p: {
  date_deb_priv: string | null;
  date_debut: string | null;
  date_debut2: string | null;
  date_debut3: string | null;
  date_debut4: string | null;
  date_fin: string | null;
}): DemarquePhase[] {
  const fin = p.date_fin ? p.date_fin.slice(0, 10) : "";
  const phases: DemarquePhase[] = [];
  if (p.date_debut)
    phases.push({
      label: "Démarque 1",
      icon: "🏷️",
      dateFrom: p.date_debut.slice(0, 10),
      dateTo: p.date_debut2 ? dayBefore(p.date_debut2) : fin,
    });
  if (p.date_debut2)
    phases.push({
      label: "Démarque 2",
      icon: "🏷️",
      dateFrom: p.date_debut2.slice(0, 10),
      dateTo: p.date_debut3 ? dayBefore(p.date_debut3) : fin,
    });
  if (p.date_debut3)
    phases.push({
      label: "Démarque 3",
      icon: "🏷️",
      dateFrom: p.date_debut3.slice(0, 10),
      dateTo: p.date_debut4 ? dayBefore(p.date_debut4) : fin,
    });
  if (p.date_debut4)
    phases.push({
      label: "Démarque 4",
      icon: "🏷️",
      dateFrom: p.date_debut4.slice(0, 10),
      dateTo: fin,
    });
  return phases;
}

// ─────────────────────────────────────────────────────────────────────────────
// SousFamilleView
// ─────────────────────────────────────────────────────────────────────────────
const SousFamilleView: React.FC = () => {
  const dispatch = useAppDispatch();

  // ── Rôle + dépôt (même logique que DashboardPage) ────────────────────────
  const { userDepot, isRestricted } = useUserScope();

  const cubeMeasures = useAppSelector((s) => s.cube.measures);
  const cubeLoading = useAppSelector((s) => s.cube.loading);
  // ✅ Charger les mesures si elles ne sont pas encore dans le store
  useEffect(() => {
    if (cubeMeasures.length === 0) {
      dispatch(fetchMeasures());
    }
  }, [dispatch, cubeMeasures.length]);

  const venteMeasures = cubeMeasures.filter((m) =>
    m.measureGroup.toLowerCase().includes("vente"),
  );
  const achatMeasures = cubeMeasures.filter((m) =>
    m.measureGroup.toLowerCase().includes("achat"),
  );
  const otherMeasures = cubeMeasures.filter(
    (m) =>
      !m.measureGroup.toLowerCase().includes("vente") &&
      !m.measureGroup.toLowerCase().includes("achat"),
  );

  const [members, setMembers] = useState<string[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [selMeasures, setSelMeasures] = useState<string[]>([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // ── Display values for masked date inputs (DD/MM/YYYY) ────────────────────
  const [dateFromDisplay, setDateFromDisplay] = React.useState(() =>
    isoToDisplay(dateFrom),
  );
  const [dateToDisplay, setDateToDisplay] = React.useState(() =>
    isoToDisplay(dateTo),
  );
  React.useEffect(() => {
    setDateFromDisplay(isoToDisplay(dateFrom));
  }, [dateFrom]);
  React.useEffect(() => {
    setDateToDisplay(isoToDisplay(dateTo));
  }, [dateTo]);

  // ── Error + shake state for invalid date inputs ───────────────────────────
  const [dateFromError, setDateFromError] = React.useState(false);
  const [dateToError, setDateToError] = React.useState(false);
  const dateFromRef = React.useRef<HTMLInputElement>(null);
  const dateToRef = React.useRef<HTMLInputElement>(null);
  // ── Refs des inputs date natifs cachés (pilotés par l'icône 📅) ──
  const dateFromPickerRef = React.useRef<HTMLInputElement>(null);
  const dateToPickerRef = React.useRef<HTMLInputElement>(null);
  const triggerShake = (
    ref: React.RefObject<HTMLInputElement | null>,
    setError: (v: boolean) => void,
  ) => {
    setError(true);
    ref.current?.classList.remove("nw-date-shake");
    void ref.current?.offsetWidth;
    ref.current?.classList.add("nw-date-shake");
    setTimeout(() => {
      ref.current?.classList.remove("nw-date-shake");
      setError(false);
    }, 400);
  };
  const [useDateFilter, setUseDateFilter] = useState(false);
  const [columns, setColumns] = useState<string[]>([]);
  const [allRows, setAllRows] = useState<any[][]>([]);
  const [rawAllCols, setRawAllCols] = useState<string[]>([]);
  const [rawAllRows, setRawAllRows] = useState<any[][]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("tableau");
  const [search, setSearch] = useState("");
  const [selMembers, setSelMembers] = useState<Set<string> | null>(null);
  const [evolutionKey, setEvolutionKey] = useState(0);

  const [topN, setTopN] = useState<number | null>(10);

  const [showSaveModal, setShowSaveModal] = useState(false);
  const [savedRefreshToken, setSavedRefreshToken] = useState(0);
  const dateInputStyle: React.CSSProperties = {
    border: `1.5px solid ${NW.gray200}`,
    borderRadius: 8,
    padding: "5px 10px",
    fontSize: 12,
    fontFamily: FONTS.body,
    color: NW.gray800,
    outline: "none",
    background: NW.white,
    transition: "border-color 0.15s",
    width: 110,
    letterSpacing: 0.5,
  };
  // ── Périodes de comparaison restaurées depuis une requête sauvegardée ─────
  const [restoredComparisonPeriods, setRestoredComparisonPeriods] =
    useState<Array<{
      label?: string;
      dateFrom: string;
      dateTo: string;
      soldeCodePeriode?: string | null;
      demarquePhase?: string | null;
    }> | null>(null);
  const [comparisonAutoRunToken, setComparisonAutoRunToken] = useState(0);
  const [hasRun, setHasRun] = useState(false);

  // ── Export hooks + state ──────────────────────────────────────────────────
  const { exportExcel } = useExportCouleurExcel();
  const { exportPDF } = useExportCouleurPDF();
  const { exportDrilldownPDF } = useExportDrilldownPDF();
  const [exportLoading, setExportLoading] = useState<"excel" | "pdf" | null>(
    null,
  );
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMenuRef = useRef<HTMLDivElement>(null);

  // ── Filename modal state ──────────────────────────────────────────────────
  const [showFilenameModal, setShowFilenameModal] = useState(false);
  const [pendingExportType, setPendingExportType] = useState<
    "excel" | "pdf" | null
  >(null);
  const [exportFilename, setExportFilename] = useState("");

  // ── Drilldown state ───────────────────────────────────────────────────────
  const [drilldownSousFamille, setDrilldownSousFamille] = useState<
    string | null
  >(null);
  const [drillExportOpen, setDrillExportOpen] = useState(false);
  const [isDrilldownExport, setIsDrilldownExport] = useState(false);

  const [filterOpen, setFilterOpen] = useState(false);
  const [filterSearch, setFilterSearch] = useState("");

  // ── Measure picker modal ──────────────────────────────────────────────────
  const [showMeasurePickerVente, setShowMeasurePickerVente] = useState(false);
  const [showMeasurePickerAchat, setShowMeasurePickerAchat] = useState(false);
  const [pickerSearch, setPickerSearch] = useState("");
  // Snapshot des mesures à l'ouverture du popup (pour "Annuler")
  const [popupInitialMeasures, setPopupInitialMeasures] = useState<string[]>(
    [],
  );

  // ── Solde / Périodes ──────────────────────────────────────────────────────
  const [soldeOpen, setSoldeOpen] = useState(false);
  const [soldePeriodes, setSoldePeriodes] = useState<SoldePeriode[]>([]);
  const [soldeLoading, setSoldeLoading] = useState(false);
  const [soldeError, setSoldeError] = useState<string | null>(null);
  const [soldeSelected, setSoldeSelected] = useState<SoldePeriode | null>(null);
  const [soldeArticleCodes, setSoldeArticleCodes] = useState<Set<string>>(
    new Set(),
  );
  const soldeDropRef = useRef<HTMLDivElement>(null);

  // ── Démarque dropdown ─────────────────────────────────────────────────────
  const [demarqueOpen, setDemarqueOpen] = useState(false);
  const [selectedDemarquePhase, setSelectedDemarquePhase] = useState<
    string | null
  >(null);
  const demarqueDropRef = useRef<HTMLDivElement>(null);

  // ── Magasin (Depot) filter dropdown — multi-sélection ────────────────────
  const [magasinOpen, setMagasinOpen] = useState(false);
  const [magasinList, setMagasinList] = useState<string[]>([]);
  const [magasinLoading, setMagasinLoading] = useState(false);
  // ✅ CHANGEMENT : Set<string> au lieu de string | null
  const [selectedMagasins, setSelectedMagasins] = useState<Set<string>>(
    new Set(),
  );
  const [magasinSearch, setMagasinSearch] = useState("");
  const magasinDropRef = useRef<HTMLDivElement>(null);

  // Si rôle restreint → forcer le dépôt dès que userDepot est connu
  useEffect(() => {
    if (isRestricted && userDepot) {
      setSelectedMagasins(new Set([userDepot]));
    }
  }, [isRestricted, userDepot]);

  // Load SousFamille members on mount
  useEffect(() => {
    setLoadingMembers(true);
    getMembers(CUBE_NAME, DIM_NAME, HIER_NAME, LEVEL_NAME)
      .then((r) => setMembers(r.data))
      .catch(() => {})
      .finally(() => setLoadingMembers(false));
  }, []);

  // ── Fermer dropdowns si clic dehors ──────────────────────────────────────
  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (
        soldeDropRef.current &&
        !soldeDropRef.current.contains(e.target as Node)
      )
        setSoldeOpen(false);
      if (
        demarqueDropRef.current &&
        !demarqueDropRef.current.contains(e.target as Node)
      )
        setDemarqueOpen(false);
      if (
        magasinDropRef.current &&
        !magasinDropRef.current.contains(e.target as Node)
      )
        setMagasinOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  // ── Chargement magasins ───────────────────────────────────────────────────
  const loadMagasins = useCallback(async () => {
    setMagasinLoading(true);
    setMagasinList([]);
    try {
      const res = await executeQuery({
        cubeName: CUBE_NAME,
        measures: ["PTTC"],
        rows: [
          {
            ...CUBE_DIMENSIONS.depot,
          },
        ],
        columns: [],
      });
      const rows: any[][] = res.data?.rows ?? [];
      const depots: string[] = rows
        .filter((row) => {
          const name = String(row[0] ?? "")
            .trim()
            .toLowerCase();
          if (!name || name === "all") return false;
          const val = row[1];
          return typeof val === "number" && Math.abs(val) > 0;
        })
        .map((row) => String(row[0] ?? "").trim());
      setMagasinList(depots);
    } catch {
      setMagasinList([]);
    } finally {
      setMagasinLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isRestricted) loadMagasins();
  }, [isRestricted]); // eslint-disable-line

  const handleMagasinOpen = () => setMagasinOpen((v) => !v);

  // ✅ CHANGEMENT : toggle checkbox au lieu de select unique
  const handleMagasinToggle = (mag: string) => {
    setSelectedMagasins((prev) => {
      const next = new Set(prev);
      if (next.has(mag)) next.delete(mag);
      else next.add(mag);
      return next;
    });
  };

  const handleMagasinClear = () => {
    setSelectedMagasins(new Set());
    setMagasinSearch("");
    setMagasinOpen(false);
  };

  const handleMagasinApply = () => {
    setMagasinOpen(false);
    setMagasinSearch("");
  };

  // ── Handlers Solde ────────────────────────────────────────────────────────
  const handleSoldeOpen = async () => {
    setSoldeOpen((v) => !v);
    if (soldePeriodes.length > 0) return;
    setSoldeLoading(true);
    setSoldeError(null);
    try {
      const { data } = await getSoldePeriodes();
      const nonVides = data.filter(
        (p: SoldePeriode) => p.date_debut && p.date_fin,
      );
      setSoldePeriodes(nonVides);
    } catch (err: any) {
      setSoldeError(err.message ?? "Erreur chargement périodes");
    } finally {
      setSoldeLoading(false);
    }
  };

  const handleSoldePeriodeSelect = async (p: SoldePeriode) => {
    setSoldeSelected(p);
    setSoldeOpen(false);
    const from = p.date_debut ? p.date_debut.slice(0, 10) : "";
    const to = p.date_fin ? p.date_fin.slice(0, 10) : "";
    setDateFrom(from);
    setDateTo(to);
    try {
      const { data: articles } = await getArticlesByPeriode(p.code_periode);
      const codes = new Set<string>(
        articles.map((a: any) => (a.cod_art ?? "").toString()).filter(Boolean),
      );
      setSoldeArticleCodes(codes);
    } catch {
      setSoldeArticleCodes(new Set());
    }
  };

  const handleSoldeClear = () => {
    setSoldeSelected(null);
    setSoldeArticleCodes(new Set());
    setDateFrom("");
    setDateTo("");
    setSelectedDemarquePhase(null);
    setDemarqueOpen(false);
  };

  // ── Mesures épinglées — même logique que MagasinView ─────────────────────
  const PINNED_VENTE_KEYS = ["qte", "remise", "phtarem", "tva", "pttc"];
  const PINNED_ACHAT_KEYS = ["qte", "remise", "phtarem", "tva", "pttc"];

  const measureGroupDefs: {
    label: string;
    color: string;
    accent: string;
    icon: string;
    items: typeof cubeMeasures;
  }[] = [
    {
      label: "Vente",
      color: NW.rose,
      accent: NW.roseLight,
      icon: "🛒",
      items: venteMeasures,
    },
    {
      label: "Achat",
      color: NW.violet,
      accent: NW.violetLight,
      icon: "📦",
      items: achatMeasures,
    },
  ];
  if (otherMeasures.length > 0) {
    measureGroupDefs.push({
      label: "Autres",
      color: NW.teal,
      accent: NW.tealLight,
      icon: "◈",
      items: otherMeasures,
    });
  }

  const pinnedGroupDefs = measureGroupDefs.map((grp, gi) => {
    const keys =
      gi === 0 ? PINNED_VENTE_KEYS : gi === 1 ? PINNED_ACHAT_KEYS : [];
    if (keys.length === 0) return { ...grp, items: grp.items };
    const pinned: typeof cubeMeasures = [];
    keys.forEach((k) => {
      const found = grp.items.find(
        (m) =>
          m.name
            .toLowerCase()
            .replace(/[\s\-]/g, "")
            .includes(k) ||
          m.caption
            .toLowerCase()
            .replace(/[\s\-]/g, "")
            .includes(k),
      );
      if (found && !pinned.includes(found)) pinned.push(found);
    });
    return {
      ...grp,
      items: pinned.length > 0 ? pinned : grp.items.slice(0, 5),
    };
  });

  // Auto-select PTTC or first vente measure
  useEffect(() => {
    if (venteMeasures.length > 0 && selMeasures.length === 0) {
      const pttc = venteMeasures.find((m) => /pttc/i.test(m.name));
      setSelMeasures([pttc ? pttc.name : venteMeasures[0].name]);
    }
  }, [venteMeasures.length]); // eslint-disable-line

  const toggleMeasure = (mName: string) => {
    setSelMeasures((prev) => {
      const next = prev.includes(mName)
        ? prev.filter((x) => x !== mName)
        : [...prev, mName];
      // Rerun automatique si "Benifice" ou "Cout Ttc" est coché/décoché
      if (/benifice/i.test(mName) || /cout.?ttc/i.test(mName)) {
        setTimeout(() => {
          run(next, dateFrom, dateTo, true, selectedMagasins);
        }, 0);
      }
      return next;
    });
  };

  // ✅ CHANGEMENT : magasinFilter est maintenant Set<string> | null
  const run = useCallback(
    async (
      measures: string[],
      from: string,
      to: string,
      useDate: boolean,
      magasinFilter?: Set<string> | null,
    ) => {
      if (measures.length === 0) return;
      setLoading(true);
      setError(null);
      try {
        const res = await executeQuery({
          cubeName: CUBE_NAME,
          measures,
          rows: [
            {
              dimensionName: DIM_NAME,
              hierarchyName: HIER_NAME,
              levelName: LEVEL_NAME,
            },
          ],
          columns: [
            {
              ...CUBE_DIMENSIONS.depot,
            },
          ],
          dateFrom: from || undefined,
          dateTo: to || undefined,
        });

        const rawCols: string[] = res.data.columns ?? [];
        const rawRows: any[][] = res.data.rows ?? [];

        setRawAllCols(rawCols);
        setRawAllRows(rawRows);

        // ✅ CHANGEMENT : filtre multi-magasins avec Set
        if (magasinFilter && magasinFilter.size > 0) {
          const filterLower = new Set(
            [...magasinFilter].map((m) => m.trim().toLowerCase()),
          );
          const keepIdx: number[] = rawCols.reduce(
            (acc: number[], col: string, i: number) => {
              if (i === 0) return acc;
              const suffix = col.split(" - ").pop()?.trim().toLowerCase() ?? "";
              if (filterLower.has(suffix)) acc.push(i);
              return acc;
            },
            [],
          );
          const filteredCols = [rawCols[0], ...keepIdx.map((i) => rawCols[i])];
          const filteredRows = rawRows.map((row) => [
            row[0],
            ...keepIdx.map((i) => row[i]),
          ]);
          setColumns(filteredCols);
          setAllRows(
            filteredRows.filter((row) => {
              const name = String(row[0] ?? "")
                .trim()
                .toLowerCase();
              return name !== "all" && name !== "unknown" && name !== "";
            }),
          );
        } else {
          // Pas de filtre — garder toutes les colonnes sauf "all" et "unknown"
          const keepIdx: number[] = rawCols.reduce(
            (acc: number[], col: string, i: number) => {
              if (i === 0) return acc;
              const suffix = col.split(" - ").pop()?.trim().toLowerCase() ?? "";
              if (suffix !== "all" && suffix !== "unknown") acc.push(i);
              return acc;
            },
            [],
          );
          const filteredCols = [rawCols[0], ...keepIdx.map((i) => rawCols[i])];
          const filteredRows =
            rawCols.length > 1
              ? rawRows.map((row) => [row[0], ...keepIdx.map((i) => row[i])])
              : rawRows;
          setColumns(filteredCols);
          setAllRows(
            filteredRows.filter((row) => {
              const name = String(row[0] ?? "")
                .trim()
                .toLowerCase();
              return name !== "all" && name !== "unknown" && name !== "";
            }),
          );

          // Mettre à jour la liste des magasins actifs
          const depotTotals: Record<string, number> = {};
          rawCols.forEach((col, ci) => {
            if (ci === 0) return;
            const depotPart = col.split(" - ").pop()?.trim() ?? "";
            if (!depotPart || depotPart.toLowerCase() === "all") return;
            let total = 0;
            rawRows.forEach((row) => {
              const v = row[ci];
              if (typeof v === "number") total += Math.abs(v);
            });
            depotTotals[depotPart] = (depotTotals[depotPart] ?? 0) + total;
          });
          setMagasinList((prev) =>
            prev.filter((mag) => (depotTotals[mag] ?? 0) > 0),
          );
        }
        setSearch("");
        setHasRun(true);
      } catch (e: any) {
        setError(e.message || "Erreur");
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  // Auto-run on mount
  useEffect(() => {
    run(selMeasures, "", "", true);
  }, []); // eslint-disable-line

  // Re-run automatiquement quand selectedMagasins change
  useEffect(() => {
    if (hasRun) {
      run(selMeasures, dateFrom, dateTo, true, selectedMagasins);
    }
  }, [selectedMagasins]); // eslint-disable-line

  const handleRun = () => {
    setSelMembers(null);
    run(selMeasures, dateFrom, dateTo, true, selectedMagasins);
  };

  // ── Export handlers ────────────────────────────────────────────────────────
  const buildExportRows = () =>
    displayRows.map((row) => ({
      couleur: String(row[0] ?? ""),
      values: dataHeaders.map((_, i) => getApplied(row, i)),
      coutTTC: hasCoutTTC ? getCoutTTC(row) : null,
      marge: hasMarge ? getMarge(row) : null,
    }));

  const handleExportExcel = () => {
    setShowExportMenu(false);
    setExportFilename(`SousFamille_${new Date().toISOString().slice(0, 10)}`);
    setPendingExportType("excel");
    setShowFilenameModal(true);
  };

  const handleExportPDF = () => {
    setShowExportMenu(false);
    setExportFilename(`SousFamille_${new Date().toISOString().slice(0, 10)}`);
    setPendingExportType("pdf");
    setShowFilenameModal(true);
  };

  const handleConfirmExport = async () => {
    if (!pendingExportType) return;
    if (!exportFilename.trim()) return;
    const filename = exportFilename.trim();
    setShowFilenameModal(false);
    setExportLoading(pendingExportType);
    // ✅ selectedMagasin string pour l'export (1 seul ou null)
    const selectedMagasinForExport =
      selectedMagasins.size === 1 ? [...selectedMagasins][0] : null;
    const payload = {
      filename,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      topN,
      headers: dataHeaders,
      rows: buildExportRows(),
      grandTotals,
      totalCoutTTC,
      totalMarge,
      hasCoutTTC,
      hasMarge,
      selectedMagasin: selectedMagasinForExport,
      soldeCode: soldeSelected?.code_periode ?? null,
      demarquePhase: selectedDemarquePhase ?? null,
      viewMode,
    };

    // Export drilldown
    if (isDrilldownExport && drilldownResult) {
      const dCols = drilldownResult.cols;
      const dRows = drilldownResult.rows;
      const dDataHeaders = dCols.slice(1);
      const dPuhtIdx = dDataHeaders.findIndex((h) => /puht/i.test(h));
      const dTvaIdx = dDataHeaders.findIndex((h) => /\btva\b/i.test(h));
      const dPttcIdx = dDataHeaders.findIndex((h) => /pttc/i.test(h));
      const dQteVenteIdx = dDataHeaders.findIndex(
        (h) => /qte/i.test(h) && !/achat/i.test(h),
      );
      const dHasCout =
        !hasCoutTTCSelected &&
        dPuhtIdx >= 0 &&
        dTvaIdx >= 0 &&
        dQteVenteIdx >= 0;
      const dHasMarge = !hasBenificeSelected && dHasCout && dPttcIdx >= 0;
      const getV = (row: any[], idx: number) => {
        const v = row[1 + idx];
        const n = typeof v === "number" ? v : parseFloat(String(v ?? "0"));
        return applyRule(
          isNaN(n) ? 0 : n,
          colHeaderShouldNegate(dDataHeaders[idx], cubeMeasures),
        ) as number;
      };
      const getCout = (row: any[]) =>
        getV(row, dQteVenteIdx) *
        (getV(row, dPuhtIdx) * ((100 + getV(row, dTvaIdx)) / 100));
      const getMarg = (row: any[]) => getV(row, dPttcIdx) - getCout(row);
      const drillRows = dRows.map((row) => ({
        couleur: String(row[0] ?? ""),
        values: dDataHeaders.map((_, i) => getV(row, i)),
        coutTTC: dHasCout ? getCout(row) : null,
        marge: dHasMarge ? getMarg(row) : null,
      }));
      const drillTotals = dDataHeaders.map((_, i) =>
        dRows.reduce((s, r) => s + getV(r, i), 0),
      );
      const drillTotalCout = dHasCout
        ? dRows.reduce((s, r) => s + getCout(r), 0)
        : null;
      const drillTotalMarge = dHasMarge
        ? dRows.reduce((s, r) => s + getMarg(r), 0)
        : null;
      const drillPayload = {
        filename,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        topN: null,
        headers: dDataHeaders,
        rows: drillRows,
        grandTotals: drillTotals,
        totalCoutTTC: drillTotalCout,
        totalMarge: drillTotalMarge,
        hasCoutTTC: dHasCout,
        hasMarge: dHasMarge,
        selectedMagasin: selectedMagasinForExport,
        soldeCode: soldeSelected?.code_periode ?? null,
        demarquePhase: selectedDemarquePhase ?? null,
        viewMode: "tableau" as ViewMode,
        isDrilldown: true,
        drilldownLabel: drilldownResult.sousFamille,
        dimensionLabel: "Dépôt",
        title: `Analyse par Sous-Famille — Détail ${drilldownResult.sousFamille}`,
        reportLabel: "Analyse SousFamille",
        dimensionName: "SousFamille",
      };
      try {
        if (pendingExportType === "excel") {
          await exportExcel(drillPayload);
        } else {
          await exportDrilldownPDF({
            filename,
            categorie: drilldownResult.sousFamille,
            logoBase64: "/nawara.jpeg",
            headers: dDataHeaders,
            rows: drillRows.map((r) => ({
              depot: r.couleur,
              values: r.values,
              coutTTC: r.coutTTC,
              marge: r.marge,
            })),
            grandTotals: drillTotals,
            totalCoutTTC: drillTotalCout,
            totalMarge: drillTotalMarge,
            hasCoutTTC: dHasCout,
            hasMarge: dHasMarge,
            dateFrom: dateFrom || undefined,
            dateTo: dateTo || undefined,
            selectedMagasin: selectedMagasinForExport,
            soldeCode: soldeSelected?.code_periode ?? null,
            demarquePhase: selectedDemarquePhase ?? null,
          });
        }
      } finally {
        setExportLoading(null);
        setPendingExportType(null);
        setIsDrilldownExport(false);
      }
      return;
    }

    try {
      if (pendingExportType === "excel") await exportExcel(payload);
      else await exportPDF(payload);
    } finally {
      setExportLoading(null);
      setPendingExportType(null);
    }
  };

  const setQuickPeriod = (type: "week" | "month" | "year") => {
    const d = new Date();
    const from = new Date(d);
    if (type === "week") from.setDate(d.getDate() - 7);
    else if (type === "month") from.setMonth(d.getMonth() - 1);
    else from.setFullYear(d.getFullYear() - 1);
    setDateFrom(from.toISOString().slice(0, 10));
    setDateTo(d.toISOString().slice(0, 10));
  };

  const allRowNames = allRows
    .map((r) => String(r[0] ?? ""))
    .filter((n) => n !== "");

  const rowNamesWithData = new Set(
    allRows
      .filter((row) =>
        row.slice(1).some((val) => {
          if (val == null || val === "") return false;
          const n = parseFloat(String(val));
          return !isNaN(n) && n !== 0;
        }),
      )
      .map((r) => String(r[0] ?? "")),
  );

  const sousFamilleList = (members.length > 0 ? members : allRowNames).filter(
    (m) => rowNamesWithData.has(m),
  );

  const toggleMember = (m: string) => {
    const base =
      selMembers === null ? new Set(sousFamilleList) : new Set(selMembers);
    if (base.has(m)) base.delete(m);
    else base.add(m);
    setSelMembers(new Set(base));
  };

  // ── Data derived ────────────────────────────────────────────────────────────
  const dataHeaders = columns.slice(1);

  const negateFlags = dataHeaders.map((h) =>
    colHeaderShouldNegate(h, cubeMeasures),
  );

  const puhtAchatIdx = useMemo(
    () => dataHeaders.findIndex((h) => /puht/i.test(h)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dataHeaders.join("|")],
  );
  const tvaAchatIdx = useMemo(
    () => dataHeaders.findIndex((h) => /\btva\b/i.test(h)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dataHeaders.join("|")],
  );
  const pttcIdx = useMemo(
    () => dataHeaders.findIndex((h) => /pttc/i.test(h)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dataHeaders.join("|")],
  );
  const qteVenteIdx = useMemo(
    () => dataHeaders.findIndex((h) => /qte/i.test(h) && !/achat/i.test(h)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dataHeaders.join("|")],
  );

  // ── Détection des mesures cube directes (Cout Ttc / Benifice) ────────────
  const hasCoutTTCSelected = selMeasures.some((m) => /cout.?ttc/i.test(m));
  const hasBenificeSelected = selMeasures.some((m) => /benifice/i.test(m));

  // Si la mesure "Cout Ttc" est sélectionnée directement depuis le cube,
  // on ne calcule PAS la colonne Coût TTC (évite le doublon)
  const hasCoutTTC =
    !hasCoutTTCSelected &&
    puhtAchatIdx >= 0 &&
    tvaAchatIdx >= 0 &&
    qteVenteIdx >= 0;

  // Si la mesure "Benifice" est sélectionnée directement depuis le cube,
  // on ne calcule PAS la colonne Marge/Bénéfice (évite le doublon)
  const hasMarge = !hasBenificeSelected && hasCoutTTC && pttcIdx >= 0;

  const getApplied = (row: any[], idx: number): number => {
    if (idx < 0) return 0;
    const applied = applyRule(row[1 + idx], negateFlags[idx]);
    return typeof applied === "number"
      ? applied
      : parseFloat(String(applied)) || 0;
  };

  const getCoutTTC = (row: any[]): number => {
    const qte = getApplied(row, qteVenteIdx);
    const puht = getApplied(row, puhtAchatIdx);
    const tva = getApplied(row, tvaAchatIdx);
    return qte * (puht * ((100 + tva) / 100));
  };

  const getMarge = (row: any[]): number =>
    getApplied(row, pttcIdx) - getCoutTTC(row);

  // ── Drilldown result (détail par dépôt pour une sous-famille) ─────────────
  const drilldownResult = useMemo(() => {
    if (!drilldownSousFamille || rawAllCols.length === 0) return null;
    const catRow = rawAllRows.find((r) => {
      const name = String(r[0] ?? "")
        .trim()
        .toLowerCase();
      if (name === "all" || name === "unknown") return false;
      return name === drilldownSousFamille.trim().toLowerCase();
    });
    if (!catRow) return null;
    const colMeta: { measure: string; depot: string; idx: number }[] = [];
    rawAllCols.forEach((col, ci) => {
      if (ci === 0) return;
      const lastDash = col.lastIndexOf(" - ");
      if (lastDash < 0) return;
      const depot = col.slice(lastDash + 3).trim();
      const measure = col.slice(0, lastDash).trim();
      if (!depot || depot.toLowerCase() === "all") return;
      colMeta.push({ measure, depot, idx: ci });
    });
    if (colMeta.length === 0) {
      return {
        cols: ["Dépôt", ...rawAllCols.slice(1)],
        rows: [["(global)", ...catRow.slice(1)]],
        sousFamille: drilldownSousFamille,
      };
    }
    const depots = [...new Set(colMeta.map((c) => c.depot))].filter((d) => {
      const n = d.toLowerCase();
      return n !== "unknown" && n !== "" && n !== "inconnu" && n !== "inconnue";
    });
    const drillMeasures = [...new Set(colMeta.map((c) => c.measure))];
    const rows: any[][] = depots.map((dep) => {
      const r: any[] = [dep];
      drillMeasures.forEach((meas) => {
        const meta = colMeta.find(
          (c) =>
            c.depot.toLowerCase() === dep.toLowerCase() &&
            c.measure.toLowerCase() === meas.toLowerCase(),
        );
        r.push(meta != null ? catRow[meta.idx] : null);
      });
      return r;
    });
    const nonEmptyRows = rows.filter((row) =>
      row.slice(1).some((v) => {
        if (v == null || v === "") return false;
        const n = typeof v === "number" ? v : parseFloat(String(v));
        return !isNaN(n) && n !== 0;
      }),
    );
    return {
      cols: ["Dépôt", ...drillMeasures],
      rows: nonEmptyRows.length > 0 ? nonEmptyRows : rows,
      sousFamille: drilldownSousFamille,
    };
  }, [drilldownSousFamille, rawAllCols, rawAllRows]);

  const filteredRows = allRows.filter((row) => {
    const rowName = String(row[0] ?? "").toLowerCase();
    if (search && !rowName.includes(search.toLowerCase())) return false;
    if (selMembers !== null && !selMembers.has(String(row[0] ?? "")))
      return false;
    const hasData = row.slice(1).some((val) => {
      if (val == null || val === "") return false;
      const n = parseFloat(String(val));
      return !isNaN(n) && n !== 0;
    });
    if (!hasData) return false;
    return true;
  });

  const displayRows = (() => {
    const sorted = [...filteredRows].sort((a, b) => {
      const av = getApplied(a, 0);
      const bv = getApplied(b, 0);
      return bv - av;
    });
    return topN ? sorted.slice(0, topN) : sorted;
  })();

  const chartData = displayRows.map((row) => {
    const entry: { name: string; [k: string]: any } = {
      name: String(row[0] ?? ""),
    };
    dataHeaders.forEach((h, i) => {
      entry[h] = getApplied(row, i);
    });
    if (hasCoutTTC) entry["Coût TTC"] = getCoutTTC(row);
    if (hasMarge) entry["Marge"] = getMarge(row);
    return entry;
  });

  const grandTotals = dataHeaders.map((_, i) =>
    displayRows.reduce((sum, row) => sum + getApplied(row, i), 0),
  );
  const totalCoutTTC = hasCoutTTC
    ? displayRows.reduce((sum, row) => sum + getCoutTTC(row), 0)
    : null;
  const totalMarge = hasMarge
    ? displayRows.reduce((sum, row) => sum + getMarge(row), 0)
    : null;

  const dashboardData = { columns, rows: allRows };

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div
      style={{
        fontFamily: FONTS.body,
        padding: "18px 20px",
        maxWidth: 1500,
        background: NW.gray50,
        minHeight: "100vh",
      }}
    >
      <style>{`
        @keyframes nwFadeSlideIn {
          from { opacity: 0; transform: translateY(12px) scale(0.99); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes nwPulseGreen {
          0%, 100% { box-shadow: 0 0 0 0 rgba(0,160,140,0.4); }
          50% { box-shadow: 0 0 0 6px rgba(0,160,140,0); }
        }
        @keyframes nwPulseRed {
          0%, 100% { box-shadow: 0 0 0 0 rgba(214,69,69,0.4); }
          50% { box-shadow: 0 0 0 6px rgba(214,69,69,0); }
        }
        @keyframes nwDateShake {
          0%{transform:translateX(0);} 15%{transform:translateX(-5px);} 30%{transform:translateX(5px);}
          45%{transform:translateX(-4px);} 60%{transform:translateX(4px);} 75%{transform:translateX(-2px);}
          90%{transform:translateX(2px);} 100%{transform:translateX(0);}
        }
        .nw-date-shake { animation: nwDateShake 0.35s ease; }
        .nw-chip-btn:hover { filter: brightness(1.12); transform: translateY(-1px); }
        .nw-row-hover:hover td { background: ${NW.indigoLight} !important; }
        .nw-dropdown-item:hover { background: rgba(29,91,168,0.07) !important; }
        .nw-view-btn { transition: all 0.15s; }
        .nw-view-btn:hover { filter: brightness(1.08); }
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: ${NW.indigoMed}; border-radius: 99px; }
      `}</style>

      {/* ══ PAGE HEADER ═══════════════════════════════════════════════════ */}
      <div
        style={{
          background: `linear-gradient(135deg, ${NW.indigoDark} 0%, ${NW.indigo} 55%, #4A86C7 100%)`,
          borderRadius: 16,
          padding: "18px 24px",
          marginBottom: 16,
          display: "flex",
          alignItems: "center",
          gap: 16,
          boxShadow: "0 6px 32px rgba(29,91,168,0.28)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 2,
            background: `linear-gradient(90deg, transparent, #B0CBEB80, #B0CBEBCC, #B0CBEB80, transparent)`,
            pointerEvents: "none",
          }}
        />

        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: 14,
            background: "rgba(255,255,255,0.12)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 24,
            flexShrink: 0,
            boxShadow: `0 0 0 1px rgba(255,255,255,0.15)`,
          }}
        >
          🗂️
        </div>

        <div>
          <div
            style={{
              fontFamily: FONTS.heading,
              fontWeight: 800,
              fontSize: 20,
              color: "#FFFFFF",
              letterSpacing: -0.4,
            }}
          >
            Analyse par Sous-Famille
          </div>
          <div
            style={{
              fontSize: 11,
              color: "rgba(255,255,255,0.5)",
              fontWeight: 600,
              letterSpacing: 2,
              textTransform: "uppercase",
              marginTop: 2,
            }}
          >
            Ventes · Marges · Performance Sous-Famille
          </div>
        </div>

        {/* KPI pills */}
        {allRows.length > 0 && (
          <div
            style={{
              marginLeft: "auto",
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                textAlign: "center",
                background: "rgba(255,255,255,0.10)",
                border: "1px solid rgba(255,255,255,0.15)",
                borderRadius: 12,
                padding: "8px 18px",
              }}
            >
              <div
                style={{
                  fontSize: 22,
                  fontWeight: 800,
                  color: "#FFFFFF",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {allRows.length}
              </div>
              <div
                style={{
                  fontSize: 10,
                  color: "rgba(255,255,255,0.55)",
                  textTransform: "uppercase",
                  letterSpacing: 1.2,
                }}
              >
                Sous-Familles
              </div>
            </div>
            {totalCoutTTC !== null && (
              <div
                style={{
                  textAlign: "center",
                  background: "rgba(0,160,140,0.18)",
                  border: "1px solid rgba(0,160,140,0.35)",
                  borderRadius: 12,
                  padding: "8px 18px",
                }}
              >
                <div
                  style={{
                    fontSize: 16,
                    fontWeight: 800,
                    color: "#A9DFD7",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {fmtNum(totalCoutTTC)}
                </div>
                <div
                  style={{
                    fontSize: 10,
                    color: "rgba(255,255,255,0.55)",
                    textTransform: "uppercase",
                    letterSpacing: 1.2,
                  }}
                >
                  Coût TTC
                </div>
              </div>
            )}
            {totalMarge !== null && (
              <div
                style={{
                  textAlign: "center",
                  background:
                    totalMarge >= 0
                      ? "rgba(0,160,140,0.18)"
                      : "rgba(214,69,69,0.18)",
                  border: `1px solid ${totalMarge >= 0 ? "rgba(0,160,140,0.4)" : "rgba(214,69,69,0.4)"}`,
                  borderRadius: 12,
                  padding: "8px 18px",
                }}
              >
                <div
                  style={{
                    fontSize: 16,
                    fontWeight: 800,
                    color: totalMarge >= 0 ? "#7FC8BD" : "#D64545",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {totalMarge >= 0 ? "+" : "-"}
                  {fmtNum(Math.abs(totalMarge))}
                </div>
                <div
                  style={{
                    fontSize: 10,
                    color: "rgba(255,255,255,0.55)",
                    textTransform: "uppercase",
                    letterSpacing: 1.2,
                  }}
                >
                  Marge Totale
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ══ CONTROLS CARD ═════════════════════════════════════════════════ */}
      <div style={card({ padding: "16px 20px", marginBottom: 14 })}>
        <div
          style={{
            display: "flex",
            gap: 16,
            flexWrap: "wrap",
            alignItems: "flex-end",
          }}
        >
          {/* ── Measure groups (pinned) + chips extras + bouton + ───────── */}
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {pinnedGroupDefs.map((grp, gi) => {
              const isVente = gi === 0;
              const isAchat = gi === 1;

              const fullGrp = measureGroupDefs[gi];
              const pinnedNames = new Set(grp.items.map((p) => p.name));
              const extraSelected = (fullGrp?.items ?? []).filter(
                (m) => !pinnedNames.has(m.name) && selMeasures.includes(m.name),
              );
              const extraCount = extraSelected.length;

              const openPicker = () => {
                setPickerSearch("");
                setPopupInitialMeasures([...selMeasures]);
                if (isVente) setShowMeasurePickerVente(true);
                else if (isAchat) setShowMeasurePickerAchat(true);
              };
              return grp.items.length > 0 ? (
                <div key={grp.label}>
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: grp.color,
                      textTransform: "uppercase",
                      letterSpacing: 1.5,
                      marginBottom: 6,
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <span>{grp.icon}</span>
                    {grp.label}
                    {extraCount > 0 && (
                      <span
                        style={{
                          background: grp.color,
                          color: "#FFFFFF",
                          borderRadius: 99,
                          fontSize: 9,
                          fontWeight: 800,
                          padding: "1px 6px",
                          lineHeight: "14px",
                        }}
                      >
                        {extraCount}✓
                      </span>
                    )}
                  </div>

                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 5,
                      alignItems: "center",
                    }}
                  >
                    {/* Pinned measures */}
                    {grp.items.map((m) => {
                      const checked = selMeasures.includes(m.name);
                      return (
                        <label
                          key={m.name}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 5,
                            fontSize: 12,
                            cursor: "pointer",
                            background: checked ? grp.accent : NW.white,
                            border: `1.5px solid ${checked ? grp.color : NW.gray200}`,
                            borderRadius: 9,
                            padding: "4px 11px",
                            color: checked ? grp.color : NW.gray700,
                            fontWeight: checked ? 700 : 400,
                            transition: "all 0.15s",
                            boxShadow: checked
                              ? `0 0 0 2px ${grp.color}22`
                              : undefined,
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleMeasure(m.name)}
                            style={{ accentColor: grp.color, margin: 0 }}
                          />
                          {m.caption}
                        </label>
                      );
                    })}

                    {/* Extra measures selected from popup — chips avec × */}
                    {extraSelected.map((m) => (
                      <span
                        key={m.name}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 5,
                          fontSize: 12,
                          fontWeight: 700,
                          background: grp.color,
                          color: "#FFFFFF",
                          borderRadius: 9,
                          padding: "4px 10px",
                          boxShadow: `0 2px 8px ${grp.color}44`,
                          transition: "all 0.15s",
                        }}
                      >
                        {m.caption}
                        <button
                          onClick={() => toggleMeasure(m.name)}
                          title="Retirer cette mesure"
                          style={{
                            background: "rgba(255,255,255,0.25)",
                            border: "none",
                            borderRadius: 5,
                            color: "#FFFFFF",
                            cursor: "pointer",
                            fontSize: 13,
                            lineHeight: 1,
                            padding: "0 3px",
                            fontWeight: 700,
                          }}
                        >
                          ×
                        </button>
                      </span>
                    ))}

                    {/* ── Bouton + pour ouvrir le picker ── */}
                    {(isVente || isAchat) && (
                      <button
                        onClick={openPicker}
                        title={`Choisir d'autres mesures ${grp.label}`}
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: 8,
                          border: `1.5px dashed ${grp.color}88`,
                          background: NW.white,
                          color: grp.color,
                          fontSize: 17,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 700,
                          transition: "all 0.15s",
                          flexShrink: 0,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = isVente
                            ? NW.roseLight
                            : NW.violetLight;
                          e.currentTarget.style.borderStyle = "solid";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = NW.white;
                          e.currentTarget.style.borderStyle = "dashed";
                        }}
                      >
                        +
                      </button>
                    )}
                  </div>
                </div>
              ) : null;
            })}
          </div>

          <div
            style={{
              width: 1,
              height: 50,
              background: NW.gray200,
              flexShrink: 0,
              alignSelf: "center",
            }}
          />

          {/* ── Date filter ─────────────────────────────────────────────── */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <span
              style={{
                fontFamily: FONTS.heading,
                fontWeight: 700,
                fontSize: 12,
                color: NW.gray800,
              }}
            >
              📅 Période
            </span>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 5,
                fontSize: 12,
                color: NW.gray500,
              }}
            >
              De :
              <div style={{ position: "relative", display: "inline-flex" }}>
                <input
                  ref={dateFromRef}
                  type="text"
                  inputMode="numeric"
                  placeholder="JJ/MM/AAAA"
                  maxLength={10}
                  value={dateFromDisplay}
                  onFocus={() => dateFromPickerRef.current?.blur()}
                  onChange={(e) => {
                    const masked = applyDateMask(e.target.value);
                    setDateFromDisplay(masked);
                    if (dateFromError) setDateFromError(false);
                    if (masked.length === 10) {
                      const iso = displayToIso(masked);
                      const fixed = fixYear(iso);
                      if (isValidDate(fixed)) {
                        const today = new Date().toISOString().slice(0, 10);
                        const capped = fixed > today ? today : fixed;
                        setDateFrom(capped);
                        if (dateTo && capped > dateTo) setDateTo("");
                      }
                    }
                  }}
                  onBlur={() => {
                    if (dateFromDisplay.length === 10) {
                      const iso = displayToIso(dateFromDisplay);
                      const fixed = fixYear(iso);
                      if (isValidDate(fixed)) {
                        const today = new Date().toISOString().slice(0, 10);
                        const capped = fixed > today ? today : fixed;
                        setDateFrom(capped);
                        if (dateTo && capped > dateTo) setDateTo("");
                      } else {
                        triggerShake(dateFromRef, setDateFromError);
                        setDateFrom("");
                      }
                      setDateFromDisplay(isoToDisplay(dateFrom));
                    } else if (dateFromDisplay === "") {
                      setDateFrom("");
                    } else if (dateFromDisplay.length > 0) {
                      triggerShake(dateFromRef, setDateFromError);
                      setDateFromDisplay(isoToDisplay(dateFrom));
                    }
                  }}
                  style={{
                    ...inputStyle,
                    width: 110,
                    paddingRight: 26,
                    letterSpacing: 0.5,
                    borderColor: dateFromError
                      ? NW.danger
                      : dateFromDisplay.length > 0 &&
                          dateFromDisplay.length < 10
                        ? NW.amber
                        : dateFrom
                          ? NW.violet
                          : NW.gray200,
                    boxShadow: dateFromError
                      ? `0 0 0 2px ${NW.danger}33`
                      : undefined,
                  }}
                />
                {/* 📅 Icône — ouvre le calendrier natif */}
                <button
                  type="button"
                  onClick={() => {
                    const el = dateFromPickerRef.current;
                    if (!el) return;
                    if (typeof (el as any).showPicker === "function")
                      (el as any).showPicker();
                    else el.focus();
                  }}
                  title="Ouvrir le calendrier"
                  style={{
                    position: "absolute",
                    right: 4,
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontSize: 13,
                    padding: 2,
                    lineHeight: 1,
                    color: NW.violet,
                  }}
                >
                  📅
                </button>
                {/* Input date natif caché — pilote le calendrier */}
                <input
                  ref={dateFromPickerRef}
                  type="date"
                  value={dateFrom || ""}
                  max={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => {
                    const iso = e.target.value;
                    if (!iso) return;
                    const today = new Date().toISOString().slice(0, 10);
                    const capped = iso > today ? today : iso;
                    setDateFrom(capped);
                    if (dateTo && capped > dateTo) setDateTo("");
                    setDateFromDisplay(isoToDisplay(capped));
                  }}
                  style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    opacity: 0,
                    pointerEvents: "none",
                    border: "none",
                  }}
                  tabIndex={-1}
                />
              </div>
            </label>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 5,
                fontSize: 12,
                color: NW.gray500,
              }}
            >
              À :
              <div style={{ position: "relative", display: "inline-flex" }}>
                <input
                  ref={dateToRef}
                  type="text"
                  inputMode="numeric"
                  placeholder="JJ/MM/AAAA"
                  maxLength={10}
                  value={dateToDisplay}
                  onFocus={() => dateToPickerRef.current?.blur()}
                  onChange={(e) => {
                    const masked = applyDateMask(e.target.value);
                    setDateToDisplay(masked);
                    if (dateToError) setDateToError(false);
                    if (masked.length === 10) {
                      const iso = displayToIso(masked);
                      const fixed = fixYear(iso);
                      if (isValidDate(fixed)) {
                        const today = new Date().toISOString().slice(0, 10);
                        const capped = fixed > today ? today : fixed;
                        if (!dateFrom || capped >= dateFrom) {
                          setDateTo(capped);
                        }
                      }
                    }
                  }}
                  onBlur={() => {
                    if (dateToDisplay.length === 10) {
                      const iso = displayToIso(dateToDisplay);
                      const fixed = fixYear(iso);
                      if (isValidDate(fixed)) {
                        const today = new Date().toISOString().slice(0, 10);
                        const capped = fixed > today ? today : fixed;
                        if (!dateFrom || capped >= dateFrom) {
                          setDateTo(capped);
                        } else {
                          triggerShake(dateToRef, setDateToError);
                          setDateTo("");
                        }
                      } else {
                        triggerShake(dateToRef, setDateToError);
                        setDateTo("");
                      }
                      setDateToDisplay(isoToDisplay(dateTo));
                    } else if (dateToDisplay === "") {
                      setDateTo("");
                    } else if (dateToDisplay.length > 0) {
                      triggerShake(dateToRef, setDateToError);
                      setDateToDisplay(isoToDisplay(dateTo));
                    }
                  }}
                  style={{
                    ...dateInputStyle,
                    paddingRight: 26,
                    borderColor: dateToError
                      ? NW.danger
                      : dateToDisplay.length > 0 && dateToDisplay.length < 10
                        ? NW.amber
                        : dateTo
                          ? NW.violet
                          : NW.gray200,
                    boxShadow: dateToError
                      ? `0 0 0 2px ${NW.danger}33`
                      : undefined,
                  }}
                />
                {/* 📅 Icône — ouvre le calendrier natif */}
                <button
                  type="button"
                  onClick={() => {
                    const el = dateToPickerRef.current;
                    if (!el) return;
                    if (typeof (el as any).showPicker === "function")
                      (el as any).showPicker();
                    else el.focus();
                  }}
                  title="Ouvrir le calendrier"
                  style={{
                    position: "absolute",
                    right: 4,
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontSize: 13,
                    padding: 2,
                    lineHeight: 1,
                    color: NW.rose,
                  }}
                >
                  📅
                </button>
                {/* Input date natif caché — pilote le calendrier */}
                <input
                  ref={dateToPickerRef}
                  type="date"
                  value={dateTo || ""}
                  min={dateFrom || undefined}
                  max={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => {
                    const iso = e.target.value;
                    if (!iso) return;
                    const today = new Date().toISOString().slice(0, 10);
                    const capped = iso > today ? today : iso;
                    if (!dateFrom || capped >= dateFrom) {
                      setDateTo(capped);
                      setDateToDisplay(isoToDisplay(capped));
                    }
                  }}
                  style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    opacity: 0,
                    pointerEvents: "none",
                    border: "none",
                  }}
                  tabIndex={-1}
                />
              </div>
            </label>
            {(["week", "month", "year"] as const).map((t) => (
              <button
                key={t}
                onClick={() => setQuickPeriod(t)}
                style={{
                  fontSize: 11,
                  padding: "3px 11px",
                  borderRadius: 99,
                  cursor: "pointer",
                  border: `1px solid ${NW.violet}55`,
                  background: NW.violetLight,
                  color: NW.violet,
                  fontWeight: 700,
                  fontFamily: FONTS.body,
                }}
              >
                {t === "week" ? "7j" : t === "month" ? "1m" : "1an"}
              </button>
            ))}
            {(dateFrom || dateTo) && (
              <button
                onClick={() => {
                  setDateFrom("");
                  setDateTo("");
                }}
                style={{
                  fontSize: 11,
                  padding: "3px 10px",
                  borderRadius: 8,
                  border: `1.5px solid ${NW.gray200}`,
                  background: NW.white,
                  color: NW.gray500,
                  cursor: "pointer",
                  fontFamily: FONTS.body,
                }}
              >
                ✕ Effacer
              </button>
            )}

            {/* ── 🏷️ Solde / Périodes ─────────────────────────────────────── */}
            <div
              ref={soldeDropRef}
              style={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <span
                style={{
                  fontFamily: FONTS.heading,
                  fontWeight: 700,
                  fontSize: 12,
                  color: NW.gray800,
                }}
              >
                🏷️ Solde
              </span>
              <button
                onClick={handleSoldeOpen}
                style={{
                  fontSize: 11,
                  padding: "4px 13px",
                  borderRadius: 99,
                  cursor: "pointer",
                  border: `1.5px solid ${soldeSelected ? "#00A08C" : NW.gray200}`,
                  background: soldeSelected ? "#D3EFEA" : NW.white,
                  color: soldeSelected ? "#006357" : NW.gray500,
                  fontWeight: soldeSelected ? 700 : 500,
                  transition: "all 0.15s",
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                  whiteSpace: "nowrap",
                  fontFamily: FONTS.body,
                }}
              >
                {soldeSelected ? soldeSelected.code_periode : "Choisir période"}
                <span style={{ fontSize: 9, opacity: 0.6 }}>▾</span>
              </button>
              {soldeSelected && (
                <button
                  onClick={handleSoldeClear}
                  title="Effacer la sélection solde"
                  style={{
                    fontSize: 11,
                    padding: "3px 9px",
                    borderRadius: 99,
                    cursor: "pointer",
                    border: `1px solid #A9DFD7`,
                    background: "#EDF8F6",
                    color: "#006357",
                    fontWeight: 700,
                    fontFamily: FONTS.body,
                  }}
                >
                  ✕
                </button>
              )}
              {soldeSelected && soldeArticleCodes.size > 0 && (
                <span
                  style={{
                    background: "#D3EFEA",
                    color: "#006357",
                    border: "1px solid #A9DFD7",
                    borderRadius: 99,
                    padding: "2px 10px",
                    fontSize: 11,
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                  }}
                >
                  🏷️ {soldeArticleCodes.size} articles
                </span>
              )}

              {/* Dropdown périodes */}
              {soldeOpen && (
                <div
                  style={{
                    position: "absolute",
                    top: "calc(100% + 8px)",
                    left: 0,
                    background: NW.white,
                    borderRadius: 12,
                    boxShadow: "0 8px 30px rgba(29,91,168,0.15)",
                    minWidth: 300,
                    maxHeight: 340,
                    overflowY: "auto",
                    zIndex: 9999,
                    border: `1.5px solid #A9DFD7`,
                  }}
                >
                  <div
                    style={{
                      padding: "10px 16px",
                      background: "linear-gradient(135deg, #006357, #00A08C)",
                      borderRadius: "10px 10px 0 0",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <span style={{ fontSize: 14 }}>🏷️</span>
                    <span
                      style={{
                        fontFamily: FONTS.heading,
                        fontWeight: 700,
                        fontSize: 12,
                        color: "#FFFFFF",
                      }}
                    >
                      Périodes de Solde
                    </span>
                    <span
                      style={{
                        fontSize: 10,
                        color: "rgba(255,255,255,0.6)",
                        marginLeft: 4,
                      }}
                    >
                      (dates auto + filtre articles)
                    </span>
                  </div>
                  {soldeLoading ? (
                    <div
                      style={{
                        padding: 20,
                        textAlign: "center",
                        color: NW.gray500,
                        fontSize: 12,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 8,
                      }}
                    >
                      Chargement des périodes…
                    </div>
                  ) : soldeError ? (
                    <div
                      style={{
                        padding: 16,
                        color: NW.danger,
                        fontSize: 12,
                        background: NW.dangerLight,
                        margin: 10,
                        borderRadius: 8,
                      }}
                    >
                      ⚠ {soldeError}
                    </div>
                  ) : soldePeriodes.length === 0 ? (
                    <div
                      style={{
                        padding: 20,
                        textAlign: "center",
                        color: NW.gray500,
                        fontSize: 12,
                        fontStyle: "italic",
                      }}
                    >
                      Aucune période de solde disponible
                    </div>
                  ) : (
                    soldePeriodes.map((p) => {
                      const isActive =
                        soldeSelected?.code_periode === p.code_periode;
                      const fmtD = (d: string | null) =>
                        d ? new Date(d).toLocaleDateString("fr-FR") : "—";
                      return (
                        <div
                          key={p.code_periode}
                          onClick={() => handleSoldePeriodeSelect(p)}
                          style={{
                            padding: "10px 16px",
                            cursor: "pointer",
                            background: isActive ? "#D3EFEA" : NW.white,
                            borderBottom: `1px solid ${NW.gray100}`,
                            transition: "background 0.1s",
                            display: "flex",
                            flexDirection: "column",
                            gap: 3,
                          }}
                          onMouseEnter={(e) =>
                            ((e.currentTarget as HTMLElement).style.background =
                              isActive ? "#A9DFD7" : "#F8FAFC")
                          }
                          onMouseLeave={(e) =>
                            ((e.currentTarget as HTMLElement).style.background =
                              isActive ? "#D3EFEA" : NW.white)
                          }
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            {isActive && (
                              <span style={{ color: "#00A08C", fontSize: 12 }}>
                                ✓
                              </span>
                            )}
                            <span
                              style={{
                                fontWeight: 700,
                                fontSize: 12,
                                color: isActive ? "#006357" : NW.gray800,
                              }}
                            >
                              {p.code_periode}
                            </span>
                          </div>
                          <span
                            style={{
                              fontSize: 10,
                              color: NW.gray500,
                              marginLeft: isActive ? 20 : 0,
                            }}
                          >
                            {fmtD(p.date_debut)} → {fmtD(p.date_fin)}
                          </span>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>

            {/* ── 📉 Démarque ─────────────────────────────────────────────── */}
            <div
              ref={demarqueDropRef}
              style={{
                position: "relative",
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              <button
                onClick={() => soldeSelected && setDemarqueOpen((v) => !v)}
                title={
                  soldeSelected
                    ? `Phases de démarque de ${soldeSelected.code_periode}`
                    : "Sélectionnez d'abord une période de solde"
                }
                disabled={!soldeSelected}
                style={{
                  fontSize: 11,
                  padding: "4px 13px",
                  borderRadius: 99,
                  cursor: soldeSelected ? "pointer" : "not-allowed",
                  border: `1.5px solid ${
                    selectedDemarquePhase
                      ? "#1D5BA8"
                      : demarqueOpen
                        ? "#1D5BA8"
                        : soldeSelected
                          ? "#1D5BA855"
                          : NW.gray200
                  }`,
                  background: selectedDemarquePhase
                    ? "#1D5BA8"
                    : demarqueOpen
                      ? "#D7E5F5"
                      : soldeSelected
                        ? "#F1F5F9"
                        : NW.gray100,
                  color: selectedDemarquePhase
                    ? "#FFFFFF"
                    : soldeSelected
                      ? "#184B8C"
                      : NW.gray300,
                  fontWeight: soldeSelected ? 700 : 500,
                  opacity: soldeSelected ? 1 : 0.55,
                  transition: "all 0.15s",
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                  whiteSpace: "nowrap",
                  fontFamily: FONTS.body,
                  boxShadow: selectedDemarquePhase
                    ? "0 2px 8px rgba(29,91,168,0.35)"
                    : demarqueOpen
                      ? "0 0 0 2px #D7E5F5"
                      : "none",
                }}
              >
                📉 {selectedDemarquePhase ?? "Démarque"}
                <span style={{ fontSize: 9, opacity: 0.7 }}>▾</span>
              </button>

              {selectedDemarquePhase && (
                <button
                  onClick={() => {
                    setSelectedDemarquePhase(null);
                    if (soldeSelected) {
                      setDateFrom(soldeSelected.date_debut?.slice(0, 10) ?? "");
                      setDateTo(soldeSelected.date_fin?.slice(0, 10) ?? "");
                    }
                  }}
                  title="Effacer la démarque sélectionnée"
                  style={{
                    marginLeft: 4,
                    fontSize: 11,
                    padding: "3px 9px",
                    borderRadius: 99,
                    cursor: "pointer",
                    border: "1px solid #B0CBEB",
                    background: "#D7E5F5",
                    color: "#184B8C",
                    fontWeight: 700,
                    fontFamily: FONTS.body,
                  }}
                >
                  ✕
                </button>
              )}

              {/* Dropdown démarque */}
              {demarqueOpen &&
                soldeSelected &&
                (() => {
                  const phases = buildDemarquePhases(soldeSelected);
                  return (
                    <div
                      style={{
                        position: "absolute",
                        top: "calc(100% + 8px)",
                        left: 0,
                        background: NW.white,
                        borderRadius: 12,
                        boxShadow: "0 8px 30px rgba(29,91,168,0.18)",
                        minWidth: 290,
                        zIndex: 9999,
                        border: "1.5px solid #D7E5F5",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          padding: "10px 16px",
                          background:
                            "linear-gradient(135deg, #184B8C, #184B8C)",
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <span style={{ fontSize: 14 }}>📉</span>
                        <div>
                          <div
                            style={{
                              fontFamily: FONTS.heading,
                              fontWeight: 700,
                              fontSize: 12,
                              color: "#FFFFFF",
                            }}
                          >
                            Phases de démarque
                          </div>
                          <div
                            style={{
                              fontSize: 10,
                              color: "rgba(255,255,255,0.6)",
                            }}
                          >
                            {soldeSelected.code_periode}
                          </div>
                        </div>
                      </div>

                      {phases.length === 0 ? (
                        <div
                          style={{
                            padding: "16px 16px",
                            textAlign: "center",
                            color: NW.gray500,
                            fontSize: 12,
                            fontStyle: "italic",
                          }}
                        >
                          Aucune phase de démarque pour cette période
                        </div>
                      ) : (
                        phases.map((phase, idx) => {
                          const isActive =
                            selectedDemarquePhase === phase.label;
                          return (
                            <div
                              key={idx}
                              onClick={() => {
                                setDateFrom(phase.dateFrom);
                                setDateTo(phase.dateTo);
                                setSelectedDemarquePhase(phase.label);
                                setDemarqueOpen(false);
                              }}
                              style={{
                                padding: "10px 16px",
                                cursor: "pointer",
                                display: "flex",
                                flexDirection: "column",
                                gap: 3,
                                borderBottom:
                                  idx < phases.length - 1
                                    ? `1px solid #EEF4FB`
                                    : "none",
                                background: isActive ? "#D7E5F5" : NW.white,
                                transition: "background 0.1s",
                              }}
                              onMouseEnter={(e) =>
                                ((
                                  e.currentTarget as HTMLElement
                                ).style.background = isActive
                                  ? "#D7E5F5"
                                  : "#F1F5F9")
                              }
                              onMouseLeave={(e) =>
                                ((
                                  e.currentTarget as HTMLElement
                                ).style.background = isActive
                                  ? "#D7E5F5"
                                  : NW.white)
                              }
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 8,
                                }}
                              >
                                {isActive && (
                                  <span
                                    style={{ color: "#1D5BA8", fontSize: 12 }}
                                  >
                                    ✓
                                  </span>
                                )}
                                <span
                                  style={{
                                    fontWeight: 700,
                                    fontSize: 12,
                                    color: isActive ? "#184B8C" : NW.gray800,
                                    fontFamily: FONTS.heading,
                                  }}
                                >
                                  {phase.icon} {phase.label}
                                </span>
                              </div>
                              <span
                                style={{
                                  fontSize: 10,
                                  color: NW.gray500,
                                  marginLeft: isActive ? 20 : 0,
                                }}
                              >
                                {phase.dateFrom} → {phase.dateTo}
                              </span>
                            </div>
                          );
                        })
                      )}
                    </div>
                  );
                })()}
            </div>
          </div>

          {/* ── 🏪 Filtre Magasin ─────────────────────────────────────────── */}
          <div
            ref={magasinDropRef}
            style={{
              position: "relative",
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            {isRestricted ? (
              /* ── Pill statique non-cliquable pour vendeur / responsable_boutique ── */
              <span
                style={{
                  fontSize: 11,
                  padding: "4px 13px",
                  borderRadius: 99,
                  border: `1.5px solid ${NW.indigo}`,
                  background: NW.indigo,
                  color: "#FFFFFF",
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                  whiteSpace: "nowrap",
                  fontFamily: FONTS.body,
                  userSelect: "none",
                }}
              >
                🏪 {userDepot}
              </span>
            ) : (
              <>
                <button
                  onClick={handleMagasinOpen}
                  title="Filtrer par magasin (dépôt)"
                  style={{
                    fontSize: 11,
                    padding: "4px 13px",
                    borderRadius: 99,
                    cursor: "pointer",
                    border: `1.5px solid ${selectedMagasins.size > 0 ? NW.indigo : magasinOpen ? NW.indigo : NW.gray200}`,
                    background:
                      selectedMagasins.size > 0
                        ? NW.indigo
                        : magasinOpen
                          ? NW.indigoLight
                          : NW.white,
                    color:
                      selectedMagasins.size > 0
                        ? "#FFFFFF"
                        : magasinOpen
                          ? NW.indigoDark
                          : NW.gray500,
                    fontWeight:
                      selectedMagasins.size > 0 || magasinOpen ? 700 : 500,
                    transition: "all 0.15s",
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                    whiteSpace: "nowrap",
                    fontFamily: FONTS.body,
                    boxShadow:
                      selectedMagasins.size > 0
                        ? "0 2px 8px rgba(29,91,168,0.30)"
                        : magasinOpen
                          ? "0 0 0 2px " + NW.indigoMed
                          : "none",
                  }}
                >
                  🏪{" "}
                  {selectedMagasins.size > 0
                    ? selectedMagasins.size === 1
                      ? [...selectedMagasins][0]
                      : `Magasin (${selectedMagasins.size})`
                    : magasinList.length > 0
                      ? `Magasin (${magasinList.length})`
                      : "Magasin"}
                  <span style={{ fontSize: 9, opacity: 0.7 }}>▾</span>
                </button>

                {selectedMagasins.size > 0 && (
                  <button
                    onClick={handleMagasinClear}
                    title="Effacer le filtre magasin"
                    style={{
                      marginLeft: 2,
                      fontSize: 11,
                      padding: "3px 9px",
                      borderRadius: 99,
                      cursor: "pointer",
                      border: "1px solid " + NW.indigoMed,
                      background: NW.indigoLight,
                      color: NW.indigoDark,
                      fontWeight: 700,
                      fontFamily: FONTS.body,
                    }}
                  >
                    ✕
                  </button>
                )}

                {magasinOpen && (
                  <div
                    style={{
                      position: "absolute",
                      top: "calc(100% + 8px)",
                      left: 0,
                      background: NW.white,
                      borderRadius: 12,
                      boxShadow: "0 8px 30px rgba(29,91,168,0.18)",
                      minWidth: 240,
                      zIndex: 9999,
                      border: "1.5px solid " + NW.indigoMed,
                      display: "flex",
                      flexDirection: "column",
                      overflow: "hidden",
                    }}
                  >
                    {/* Header */}
                    <div
                      style={{
                        padding: "10px 16px",
                        background: "linear-gradient(135deg, #2077BC, #1D5BA8)",
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        flexShrink: 0,
                      }}
                    >
                      <span style={{ fontSize: 14 }}>🏪</span>
                      <div>
                        <div
                          style={{
                            fontFamily: FONTS.heading,
                            fontWeight: 700,
                            fontSize: 12,
                            color: "#FFFFFF",
                          }}
                        >
                          Filtrer par Magasin
                        </div>
                        <div
                          style={{
                            fontSize: 10,
                            color: "rgba(255,255,255,0.6)",
                          }}
                        >
                          Sélectionnez un ou plusieurs dépôts
                        </div>
                      </div>
                    </div>

                    {/* Search */}
                    <div
                      style={{
                        padding: "8px 12px",
                        borderBottom: "1px solid " + NW.gray100,
                        flexShrink: 0,
                      }}
                    >
                      <input
                        type="text"
                        placeholder="Rechercher…"
                        value={magasinSearch}
                        onChange={(e) => setMagasinSearch(e.target.value)}
                        autoFocus
                        style={{
                          width: "100%",
                          border: "1.5px solid " + NW.indigoMed,
                          borderRadius: 8,
                          padding: "5px 10px",
                          fontSize: 12,
                          fontFamily: FONTS.body,
                          outline: "none",
                          boxSizing: "border-box",
                        }}
                      />
                    </div>

                    {/* List */}
                    <div style={{ overflowY: "auto", maxHeight: 220 }}>
                      {magasinLoading ? (
                        <div
                          style={{
                            padding: 20,
                            textAlign: "center",
                            color: NW.gray500,
                            fontSize: 12,
                          }}
                        >
                          ⏳ Chargement des magasins…
                        </div>
                      ) : magasinList.length === 0 ? (
                        <div
                          style={{
                            padding: 20,
                            textAlign: "center",
                            color: NW.gray500,
                            fontSize: 12,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 10,
                          }}
                        >
                          <span style={{ fontStyle: "italic" }}>
                            Aucun magasin trouvé
                          </span>
                          <button
                            onClick={loadMagasins}
                            style={{
                              fontSize: 11,
                              padding: "4px 14px",
                              borderRadius: 8,
                              border: "1.5px solid " + NW.indigoMed,
                              background: NW.indigoLight,
                              color: NW.indigoDark,
                              cursor: "pointer",
                              fontWeight: 700,
                              fontFamily: FONTS.body,
                            }}
                          >
                            🔄 Réessayer
                          </button>
                        </div>
                      ) : (
                        <>
                          {/* ✅ Sous-header "Tout sélectionner" */}
                          <div
                            style={{
                              padding: "6px 12px",
                              borderBottom: "1px solid " + NW.gray100,
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              background: NW.gray50,
                              flexShrink: 0,
                            }}
                          >
                            <label
                              style={{
                                fontSize: 11,
                                color: NW.gray500,
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                userSelect: "none",
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={
                                  magasinList
                                    .filter(
                                      (m) =>
                                        magasinSearch === "" ||
                                        m
                                          .toLowerCase()
                                          .includes(
                                            magasinSearch.toLowerCase(),
                                          ),
                                    )
                                    .every((m) => selectedMagasins.has(m)) &&
                                  magasinList.filter(
                                    (m) =>
                                      magasinSearch === "" ||
                                      m
                                        .toLowerCase()
                                        .includes(magasinSearch.toLowerCase()),
                                  ).length > 0
                                }
                                onChange={(e) => {
                                  const visible = magasinList.filter(
                                    (m) =>
                                      magasinSearch === "" ||
                                      m
                                        .toLowerCase()
                                        .includes(magasinSearch.toLowerCase()),
                                  );
                                  setSelectedMagasins((prev) => {
                                    const next = new Set(prev);
                                    if (e.target.checked)
                                      visible.forEach((m) => next.add(m));
                                    else visible.forEach((m) => next.delete(m));
                                    return next;
                                  });
                                }}
                                style={{
                                  accentColor: NW.indigo,
                                  cursor: "pointer",
                                }}
                              />
                              Tout sélectionner
                            </label>
                            {selectedMagasins.size > 0 && (
                              <span
                                style={{
                                  fontSize: 11,
                                  color: NW.indigo,
                                  fontWeight: 700,
                                }}
                              >
                                {selectedMagasins.size} sélectionné
                                {selectedMagasins.size > 1 ? "s" : ""}
                              </span>
                            )}
                          </div>

                          {/* ✅ Liste checkboxes */}
                          {magasinList
                            .filter(
                              (m) =>
                                magasinSearch === "" ||
                                m
                                  .toLowerCase()
                                  .includes(magasinSearch.toLowerCase()),
                            )
                            .map((mag) => {
                              const isActive = selectedMagasins.has(mag);
                              return (
                                <label
                                  key={mag}
                                  style={{
                                    padding: "9px 16px",
                                    cursor: "pointer",
                                    background: isActive
                                      ? NW.indigoLight
                                      : NW.white,
                                    borderBottom: "1px solid " + NW.gray100,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 10,
                                    fontSize: 12,
                                    fontWeight: isActive ? 700 : 400,
                                    color: isActive
                                      ? NW.indigoDark
                                      : NW.gray800,
                                    userSelect: "none",
                                  }}
                                  onMouseEnter={(e) =>
                                    ((
                                      e.currentTarget as HTMLElement
                                    ).style.background = isActive
                                      ? NW.indigoMed
                                      : NW.gray50)
                                  }
                                  onMouseLeave={(e) =>
                                    ((
                                      e.currentTarget as HTMLElement
                                    ).style.background = isActive
                                      ? NW.indigoLight
                                      : NW.white)
                                  }
                                >
                                  <input
                                    type="checkbox"
                                    checked={isActive}
                                    onChange={() => handleMagasinToggle(mag)}
                                    style={{
                                      accentColor: NW.indigo,
                                      width: 14,
                                      height: 14,
                                      cursor: "pointer",
                                      flexShrink: 0,
                                    }}
                                  />
                                  <span style={{ fontFamily: FONTS.body }}>
                                    {mag}
                                  </span>
                                </label>
                              );
                            })}
                        </>
                      )}
                    </div>

                    {/* ✅ Footer Appliquer */}
                    <div
                      style={{
                        padding: "8px 12px",
                        borderTop: "1px solid " + NW.gray100,
                        flexShrink: 0,
                      }}
                    >
                      <button
                        onClick={handleMagasinApply}
                        style={{
                          width: "100%",
                          padding: "7px 0",
                          borderRadius: 8,
                          border: "none",
                          background: `linear-gradient(135deg, ${NW.indigoDark}, ${NW.indigo})`,
                          color: "#FFFFFF",
                          fontWeight: 700,
                          fontSize: 12,
                          fontFamily: FONTS.body,
                          cursor: "pointer",
                        }}
                      >
                        ✓ Appliquer ({selectedMagasins.size} sélectionné
                        {selectedMagasins.size > 1 ? "s" : ""})
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <div
            style={{
              marginLeft: "auto",
              alignSelf: "flex-end",
              display: "flex",
              gap: 8,
              alignItems: "center",
            }}
          >
            <SavedQueriesDropdown
              refreshToken={savedRefreshToken}
              currentType="sousfamille"
              onSelect={(q) => {
                setSelMeasures(q.measures);
                if (q.topN != null) setTopN(q.topN);
                const validModes: ViewMode[] = [
                  "tableau",
                  "dashboard",
                  "comparaison",
                  "evolution",
                ];
                if (q.section && validModes.includes(q.section as ViewMode)) {
                  setViewMode(q.section as ViewMode);
                }
                const savedSoldeCode = (q as any).soldeCodePeriode as
                  | string
                  | null
                  | undefined;
                if (savedSoldeCode) {
                  const found = soldePeriodes.find(
                    (p) => p.code_periode === savedSoldeCode,
                  );
                  if (found) {
                    setSoldeSelected(found);
                    setDateFrom(
                      found.date_debut?.slice(0, 10) ?? q.dateFrom ?? "",
                    );
                    setDateTo(found.date_fin?.slice(0, 10) ?? q.dateTo ?? "");
                  } else {
                    setSoldeSelected({
                      code_periode: savedSoldeCode,
                      date_debut: q.dateFrom ?? null,
                      date_fin: q.dateTo ?? null,
                      date_deb_priv: null,
                      date_debut2: null,
                      date_debut3: null,
                      date_debut4: null,
                      cod_dep: null,
                    });
                    setDateFrom(q.dateFrom ?? "");
                    setDateTo(q.dateTo ?? "");
                  }
                } else {
                  setSoldeSelected(null);
                  setSoldeArticleCodes(new Set());
                  setDateFrom(q.dateFrom ?? "");
                  setDateTo(q.dateTo ?? "");
                }
                const rawPeriods = (q as any).comparisonPeriods;
                if (rawPeriods) {
                  try {
                    const parsed =
                      typeof rawPeriods === "string"
                        ? JSON.parse(rawPeriods)
                        : rawPeriods;
                    if (Array.isArray(parsed) && parsed.length > 0) {
                      setRestoredComparisonPeriods(parsed);
                      setComparisonAutoRunToken((t) => t + 1);
                    }
                  } catch {
                    setRestoredComparisonPeriods(null);
                  }
                } else {
                  setRestoredComparisonPeriods(null);
                }
                run(q.measures, q.dateFrom ?? "", q.dateTo ?? "", true);
              }}
            />

            <button
              onClick={() => hasRun && setShowSaveModal(true)}
              disabled={!hasRun}
              style={{
                padding: "9px 16px",
                background: hasRun
                  ? `linear-gradient(135deg, ${NW.indigo}, ${NW.indigoDark})`
                  : NW.gray200,
                color: hasRun ? "#FFFFFF" : NW.gray500,
                border: "none",
                borderRadius: 10,
                cursor: hasRun ? "pointer" : "not-allowed",
                fontWeight: 700,
                fontSize: 12,
                fontFamily: FONTS.body,
                boxShadow: hasRun ? `0 4px 14px rgba(29,91,168,0.28)` : "none",
                transition: "all 0.15s",
                display: "flex",
                alignItems: "center",
                gap: 6,
                whiteSpace: "nowrap",
              }}
            >
              💾 Enregistrer
            </button>

            {/* ── 📤 Exporter + ▶ Calculer ── */}
            <div
              style={{
                position: "relative",
                display: "flex",
                gap: 8,
                alignItems: "center",
              }}
            >
              {!isRestricted && (
                <>
                  <button
                    onClick={() => hasRun && setShowExportMenu((v) => !v)}
                    disabled={!hasRun || exportLoading !== null}
                    style={{
                      padding: "9px 16px",
                      background:
                        hasRun && exportLoading === null
                          ? `linear-gradient(135deg, ${NW.teal}, #00806F)`
                          : NW.gray200,
                      color:
                        hasRun && exportLoading === null ? "#FFFFFF" : NW.gray500,
                      border: "none",
                      borderRadius: 10,
                      cursor:
                        hasRun && exportLoading === null
                          ? "pointer"
                          : "not-allowed",
                      fontWeight: 700,
                      fontSize: 12,
                      fontFamily: FONTS.body,
                      boxShadow:
                        hasRun && exportLoading === null
                          ? `0 4px 14px rgba(0,160,140,0.30)`
                          : "none",
                      transition: "all 0.15s",
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {exportLoading === "excel" ? (
                      <>
                        <span
                          style={{
                            width: 13,
                            height: 13,
                            borderRadius: "50%",
                            border: "2px solid rgba(255,255,255,0.4)",
                            borderTopColor: "#FFFFFF",
                            display: "inline-block",
                            animation: "nwSpin 0.7s linear infinite",
                          }}
                        />
                        Excel…
                      </>
                    ) : exportLoading === "pdf" ? (
                      <>
                        <span
                          style={{
                            width: 13,
                            height: 13,
                            borderRadius: "50%",
                            border: "2px solid rgba(255,255,255,0.4)",
                            borderTopColor: "#FFFFFF",
                            display: "inline-block",
                            animation: "nwSpin 0.7s linear infinite",
                          }}
                        />
                        PDF…
                      </>
                    ) : (
                      <>
                        📤 Exporter{" "}
                        <span
                          style={{
                            fontSize: 10,
                            opacity: 0.8,
                            transform: showExportMenu
                              ? "rotate(180deg)"
                              : "rotate(0deg)",
                            transition: "transform 0.15s",
                            display: "inline-block",
                          }}
                        >
                          ▼
                        </span>
                      </>
                    )}
                  </button>
                  {showExportMenu && (
                    <div
                      style={{
                        position: "absolute",
                        top: "calc(100% + 6px)",
                        right: 0,
                        zIndex: 9995,
                        background: NW.white,
                        borderRadius: 12,
                        boxShadow: "0 10px 40px rgba(0,160,140,0.18)",
                        border: `1.5px solid ${NW.tealLight}`,
                        minWidth: 200,
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          background: `linear-gradient(135deg, ${NW.teal}, #00806F)`,
                          padding: "10px 14px",
                        }}
                      >
                        <div
                          style={{
                            fontFamily: FONTS.heading,
                            fontWeight: 800,
                            fontSize: 12,
                            color: "#FFFFFF",
                          }}
                        >
                          📤 Exporter les données
                        </div>
                        <div
                          style={{
                            fontSize: 9,
                            color: "rgba(255,255,255,0.65)",
                            marginTop: 2,
                          }}
                        >
                          Choisissez le format
                        </div>
                      </div>
                      <button
                        onClick={handleExportExcel}
                        style={{
                          width: "100%",
                          padding: "11px 16px",
                          border: "none",
                          borderBottom: `1px solid ${NW.gray100}`,
                          background: NW.white,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          fontFamily: FONTS.body,
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.background = NW.gray50)
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.background = NW.white)
                        }
                      >
                        <span style={{ fontSize: 20 }}>📊</span>
                        <div>
                          <div
                            style={{
                              fontWeight: 700,
                              fontSize: 12,
                              color: NW.gray800,
                            }}
                          >
                            Excel (.xlsx)
                          </div>
                          <div style={{ fontSize: 10, color: NW.gray500 }}>
                            Tableau complet + résumé
                          </div>
                        </div>
                      </button>
                      <button
                        onClick={handleExportPDF}
                        style={{
                          width: "100%",
                          padding: "11px 16px",
                          border: "none",
                          background: NW.white,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          fontFamily: FONTS.body,
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.background = NW.gray50)
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.background = NW.white)
                        }
                      >
                        <span style={{ fontSize: 20 }}>📄</span>
                        <div>
                          <div
                            style={{
                              fontWeight: 700,
                              fontSize: 12,
                              color: NW.gray800,
                            }}
                          >
                            PDF (.pdf)
                          </div>
                          <div style={{ fontSize: 10, color: NW.gray500 }}>
                            Tableau · Dashboard · Comparaison · Évolution
                          </div>
                        </div>
                      </button>
                    </div>
                  )}
                </>
              )}

              <button
                onClick={handleRun}
                disabled={loading || selMeasures.length === 0}
                style={{
                  padding: "9px 22px",
                  background:
                    loading || selMeasures.length === 0
                      ? NW.gray200
                      : NW.indigo,
                  color:
                    loading || selMeasures.length === 0 ? NW.gray500 : "#FFFFFF",
                  border: "none",
                  borderRadius: 10,
                  cursor:
                    loading || selMeasures.length === 0 ? "default" : "pointer",
                  fontWeight: 700,
                  fontSize: 13,
                  fontFamily: FONTS.body,
                  boxShadow:
                    loading || selMeasures.length === 0
                      ? "none"
                      : `0 4px 16px rgba(29,91,168,0.28)`,
                  transition: "all 0.15s",
                  display: "flex",
                  alignItems: "center",
                  gap: 7,
                }}
              >
                {loading ? (
                  <>
                    <span
                      className="spinner"
                      style={{ width: 14, height: 14, flexShrink: 0 }}
                    />
                    Chargement…
                  </>
                ) : (
                  <>▶ Calculer</>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Calculated columns info banner */}
        {(hasCoutTTC ||
          hasMarge ||
          hasCoutTTCSelected ||
          hasBenificeSelected) && (
          <div
            style={{
              marginTop: 12,
              padding: "8px 14px",
              background: NW.tealLight,
              borderRadius: 9,
              border: `1px solid ${NW.teal}33`,
              display: "flex",
              gap: 16,
              flexWrap: "wrap",
              fontSize: 11,
              fontWeight: 600,
              alignItems: "center",
            }}
          >
            <span style={{ color: NW.teal }}>
              ⚡ Colonnes calculées actives :
            </span>
            {(hasCoutTTC || hasCoutTTCSelected) && (
              <span
                style={{
                  color: NW.teal,
                  background: "rgba(0,160,140,0.12)",
                  border: `1px solid ${NW.teal}44`,
                  borderRadius: 6,
                  padding: "2px 9px",
                }}
              >
                💎 Coût TTC = Qté Vente × (PUHT Achat × (1 + TVA%))
              </span>
            )}
            {(hasMarge || hasBenificeSelected) && (
              <span
                style={{
                  color: NW.orange,
                  background: "rgba(176,115,3,0.10)",
                  border: `1px solid ${NW.orange}44`,
                  borderRadius: 6,
                  padding: "2px 9px",
                }}
              >
                📈 Bénéfice = PTTC − Coût TTC
              </span>
            )}
          </div>
        )}
      </div>

      {/* ══ ERROR ═════════════════════════════════════════════════════════ */}
      {error && (
        <div
          style={{
            color: NW.danger,
            fontSize: 13,
            padding: "10px 16px",
            background: NW.dangerLight,
            borderRadius: 10,
            marginBottom: 14,
            border: `1px solid ${NW.danger}30`,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          ⚠ {error}
        </div>
      )}

      {/* ══ RESULTS ═══════════════════════════════════════════════════════ */}
      {allRows.length > 0 && (
        <div style={card()}>
          {/* ── Beautiful Filter Bar ──────────────────────────────────── */}
          <div
            style={{
              padding: "12px 16px",
              background: NW.indigoLight,
              borderBottom: `1.5px solid ${NW.indigoMed}`,
            }}
          >
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              {/* Dropdown trigger */}
              <div style={{ position: "relative" }}>
                <button
                  onClick={() => setFilterOpen((v) => !v)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "7px 16px",
                    border: `1.5px solid ${selMembers !== null ? NW.indigo : NW.gray200}`,
                    borderRadius: 10,
                    background: selMembers !== null ? NW.indigoLight : NW.white,
                    color: selMembers !== null ? NW.indigo : NW.gray700,
                    fontWeight: 600,
                    fontSize: 12,
                    fontFamily: FONTS.body,
                    cursor: "pointer",
                    transition: "all 0.15s",
                    boxShadow: filterOpen
                      ? `0 0 0 3px ${NW.indigoMed}`
                      : undefined,
                    minWidth: 220,
                    justifyContent: "space-between",
                  }}
                >
                  <span
                    style={{ display: "flex", alignItems: "center", gap: 6 }}
                  >
                    <span style={{ fontSize: 14 }}>🗂️</span>
                    {selMembers === null
                      ? `${sousFamilleList.length} / ${sousFamilleList.length} sous-familles`
                      : `${selMembers.size} / ${sousFamilleList.length} sous-famille${selMembers.size !== 1 ? "s" : ""}`}
                  </span>
                  <span
                    style={{
                      fontSize: 11,
                      color: NW.gray500,
                      display: "inline-block",
                      transform: filterOpen ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.2s",
                    }}
                  >
                    ▾
                  </span>
                </button>

                {/* Dropdown panel */}
                {filterOpen && (
                  <div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      position: "absolute",
                      top: "calc(100% + 6px)",
                      left: 0,
                      zIndex: 200,
                      background: NW.white,
                      border: `1.5px solid ${NW.indigoMed}`,
                      borderRadius: 14,
                      boxShadow: "0 12px 40px rgba(29,91,168,0.18)",
                      width: 300,
                      maxHeight: 310,
                      display: "flex",
                      flexDirection: "column",
                      overflow: "hidden",
                      animation:
                        "nwFadeSlideIn 0.18s cubic-bezier(0.22,1,0.36,1)",
                    }}
                  >
                    <div
                      style={{
                        padding: "12px 14px 10px",
                        borderBottom: `1px solid ${NW.gray100}`,
                        background: NW.indigoLight,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: NW.indigo,
                          textTransform: "uppercase",
                          letterSpacing: 1.2,
                          marginBottom: 8,
                        }}
                      >
                        🔍 Filtrer les sous-familles
                      </div>
                      <div style={{ position: "relative" }}>
                        <span
                          style={{
                            position: "absolute",
                            left: 10,
                            top: "50%",
                            transform: "translateY(-50%)",
                            color: NW.indigo,
                            fontSize: 12,
                            pointerEvents: "none",
                          }}
                        >
                          🔍
                        </span>
                        <input
                          autoFocus
                          type="text"
                          placeholder="Rechercher une sous-famille…"
                          value={filterSearch}
                          onChange={(e) => setFilterSearch(e.target.value)}
                          style={{
                            width: "100%",
                            border: `1.5px solid ${filterSearch ? NW.indigo : NW.gray200}`,
                            borderRadius: 8,
                            padding: "6px 10px 6px 30px",
                            fontSize: 12,
                            fontFamily: FONTS.body,
                            outline: "none",
                            color: NW.gray800,
                            boxSizing: "border-box",
                            transition: "border-color 0.15s",
                            boxShadow: filterSearch
                              ? `0 0 0 3px ${NW.indigoMed}`
                              : undefined,
                          }}
                        />
                        {filterSearch && (
                          <button
                            onClick={() => setFilterSearch("")}
                            style={{
                              position: "absolute",
                              right: 8,
                              top: "50%",
                              transform: "translateY(-50%)",
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              color: NW.gray500,
                              fontSize: 15,
                              lineHeight: 1,
                              padding: 0,
                            }}
                          >
                            ×
                          </button>
                        )}
                      </div>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        gap: 6,
                        padding: "8px 12px",
                        borderBottom: `1px solid ${NW.gray100}`,
                        background: NW.gray50,
                      }}
                    >
                      <button
                        onClick={() => setSelMembers(null)}
                        style={{
                          flex: 1,
                          padding: "5px 0",
                          fontSize: 11,
                          fontWeight: 700,
                          border: `1.5px solid ${NW.indigo}44`,
                          borderRadius: 8,
                          background:
                            selMembers === null ? NW.indigo : NW.white,
                          color: selMembers === null ? "#FFFFFF" : NW.indigo,
                          cursor: "pointer",
                          fontFamily: FONTS.body,
                          transition: "all 0.12s",
                        }}
                      >
                        ✓ Tout
                      </button>
                      <button
                        onClick={() => setSelMembers(new Set())}
                        style={{
                          flex: 1,
                          padding: "5px 0",
                          fontSize: 11,
                          fontWeight: 700,
                          border: `1.5px solid ${NW.gray200}`,
                          borderRadius: 8,
                          background: NW.white,
                          color: NW.gray500,
                          cursor: "pointer",
                          fontFamily: FONTS.body,
                          transition: "all 0.12s",
                        }}
                      >
                        ✕ Aucun
                      </button>
                    </div>

                    <div
                      style={{
                        flex: 1,
                        overflowY: "auto",
                        padding: "4px 8px",
                      }}
                    >
                      {sousFamilleList
                        .filter((n) =>
                          n.toLowerCase().includes(filterSearch.toLowerCase()),
                        )
                        .map((m, idx) => {
                          const active =
                            selMembers === null || selMembers.has(m);
                          const chipColor =
                            CHIP_PALETTE[idx % CHIP_PALETTE.length];
                          return (
                            <label
                              key={m}
                              className="nw-dropdown-item"
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 9,
                                padding: "6px 6px",
                                borderRadius: 7,
                                cursor: "pointer",
                                background: active
                                  ? `${chipColor}08`
                                  : "transparent",
                                transition: "background 0.1s",
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleMember(m);
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={active}
                                onChange={() => toggleMember(m)}
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                  accentColor: chipColor,
                                  width: 15,
                                  height: 15,
                                  flexShrink: 0,
                                  cursor: "pointer",
                                }}
                              />
                              <span
                                style={{
                                  width: 9,
                                  height: 9,
                                  borderRadius: "50%",
                                  background: chipColor,
                                  flexShrink: 0,
                                  boxShadow: active
                                    ? `0 0 5px ${chipColor}66`
                                    : undefined,
                                }}
                              />
                              <span
                                style={{
                                  fontSize: 12,
                                  color: active ? NW.gray800 : NW.gray500,
                                  fontWeight: active ? 600 : 400,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  flex: 1,
                                }}
                              >
                                {m || "(vide)"}
                              </span>
                              {active && (
                                <span
                                  style={{
                                    fontSize: 11,
                                    color: chipColor,
                                    fontWeight: 700,
                                    flexShrink: 0,
                                  }}
                                >
                                  ✓
                                </span>
                              )}
                            </label>
                          );
                        })}
                      {loadingMembers && sousFamilleList.length === 0 && (
                        <div
                          style={{
                            padding: 20,
                            textAlign: "center",
                            fontSize: 12,
                            color: NW.gray500,
                            fontStyle: "italic",
                          }}
                        >
                          Chargement des sous-familles…
                        </div>
                      )}
                      {!loadingMembers &&
                        sousFamilleList.filter((n) =>
                          n.toLowerCase().includes(filterSearch.toLowerCase()),
                        ).length === 0 &&
                        filterSearch && (
                          <div
                            style={{
                              padding: 20,
                              textAlign: "center",
                              fontSize: 12,
                              color: NW.gray500,
                            }}
                          >
                            Aucun résultat pour «&nbsp;{filterSearch}&nbsp;»
                          </div>
                        )}
                    </div>

                    <div
                      style={{
                        padding: "10px 14px",
                        borderTop: `1px solid ${NW.gray100}`,
                        background: NW.gray50,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <span style={{ fontSize: 11, color: NW.gray500 }}>
                        {selMembers === null
                          ? `${sousFamilleList.length} sous-familles`
                          : `${selMembers.size} sélectionnée${selMembers.size !== 1 ? "s" : ""}`}
                      </span>
                      <button
                        onClick={() => {
                          setFilterOpen(false);
                          setFilterSearch("");
                          run(selMeasures, dateFrom, dateTo, true);
                          setEvolutionKey((k) => k + 1);
                        }}
                        style={{
                          padding: "6px 20px",
                          fontSize: 12,
                          fontWeight: 700,
                          border: "none",
                          borderRadius: 8,
                          background: NW.indigo,
                          color: "#FFFFFF",
                          cursor: "pointer",
                          fontFamily: FONTS.body,
                          boxShadow: `0 2px 10px rgba(29,91,168,0.28)`,
                        }}
                      >
                        Appliquer
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {filterOpen && (
                <div
                  onClick={() => {
                    setFilterOpen(false);
                    setFilterSearch("");
                  }}
                  style={{
                    position: "fixed",
                    inset: 0,
                    zIndex: 199,
                  }}
                />
              )}

              {/* ── Inline search ── */}
              <div style={{ position: "relative", flex: 1, maxWidth: 280 }}>
                <span
                  style={{
                    position: "absolute",
                    left: 10,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: NW.indigo,
                    fontSize: 13,
                    pointerEvents: "none",
                  }}
                >
                  🔍
                </span>
                <input
                  type="text"
                  placeholder="Rechercher une sous-famille…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "7px 10px 7px 32px",
                    border: `1.5px solid ${NW.gray200}`,
                    borderRadius: 9,
                    fontSize: 12,
                    fontFamily: FONTS.body,
                    outline: "none",
                    color: NW.gray800,
                    background: NW.white,
                    transition: "border-color 0.15s",
                    boxSizing: "border-box",
                  }}
                />
                {search && (
                  <button
                    onClick={() => setSearch("")}
                    style={{
                      position: "absolute",
                      right: 8,
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: NW.gray500,
                      fontSize: 13,
                      padding: 0,
                      lineHeight: 1,
                    }}
                  >
                    ✕
                  </button>
                )}
              </div>

              {/* ── Top N selector ── */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                  marginLeft: "auto",
                }}
              >
                <span
                  style={{
                    fontSize: 11,
                    color: NW.gray500,
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                  }}
                >
                  Top :
                </span>
                {[...TOP_OPTIONS, null].map((n) => (
                  <button
                    key={String(n)}
                    onClick={() => setTopN(n)}
                    className="nw-view-btn"
                    style={{
                      fontSize: 11,
                      padding: "4px 10px",
                      borderRadius: 7,
                      border: `1.5px solid ${topN === n ? NW.indigo : NW.gray200}`,
                      background: topN === n ? NW.indigoLight : NW.white,
                      color: topN === n ? NW.indigo : NW.gray500,
                      fontWeight: topN === n ? 700 : 500,
                      cursor: "pointer",
                      fontFamily: FONTS.body,
                    }}
                  >
                    {n === null ? "Tous" : n}
                  </button>
                ))}
              </div>
            </div>

            {/* ══ Row 2 : View mode tabs ════════════════════════════════ */}
            <div
              style={{
                display: "flex",
                gap: 6,
                marginTop: 10,
                alignItems: "center",
              }}
            >
              <span
                style={{
                  fontSize: 11,
                  color: NW.gray500,
                  fontWeight: 600,
                  marginRight: 4,
                }}
              >
                Vue :
              </span>
              {VIEW_MODES.map(({ mode, icon, label }) => (
                <button
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  className="nw-view-btn"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                    padding: "5px 13px",
                    borderRadius: 8,
                    border: `1.5px solid ${viewMode === mode ? NW.indigo : NW.gray200}`,
                    background:
                      viewMode === mode
                        ? NW.indigoLight
                        : NW.white,
                    color: viewMode === mode ? NW.indigo : NW.gray500,
                    fontWeight: viewMode === mode ? 700 : 500,
                    fontSize: 12,
                    cursor: "pointer",
                    fontFamily: FONTS.body,
                    boxShadow:
                      viewMode === mode
                        ? `0 2px 8px rgba(29,91,168,0.12)`
                        : undefined,
                    transition: "all 0.15s",
                  }}
                >
                  <span style={{ fontSize: 13 }}>{icon}</span>
                  {label}
                </button>
              ))}

              {displayRows.length > 0 && (
                <span
                  style={{
                    marginLeft: "auto",
                    fontSize: 11,
                    color: NW.gray500,
                    background: NW.gray100,
                    borderRadius: 99,
                    padding: "3px 10px",
                    fontWeight: 600,
                  }}
                >
                  {displayRows.length} sous-famille
                  {displayRows.length !== 1 ? "s" : ""}
                </span>
              )}
            </div>
          </div>

          {/* ── Tableau view ──────────────────────────────────────────── */}
          {viewMode === "tableau" && (
            <div
              style={{
                overflowX: "auto",
                overflowY: "auto",
                maxHeight: 580,
                animation: "nwFadeSlideIn 0.42s cubic-bezier(0.22,1,0.36,1)",
              }}
            >
              <table
                style={{
                  borderCollapse: "collapse",
                  fontSize: 12,
                  fontFamily: FONTS.body,
                  minWidth: 600,
                  width: "100%",
                }}
              >
                <thead style={{ position: "sticky", top: 0, zIndex: 10 }}>
                  <tr>
                    <th
                      style={thS({
                        background: NW.indigoDark,
                        color: "#FFFFFF",
                        textAlign: "left",
                        fontFamily: FONTS.heading,
                        fontSize: 12,
                        borderRight: `3px solid ${NW.indigo}`,
                        position: "sticky",
                        left: 0,
                        zIndex: 20,
                        boxShadow: "2px 0 6px rgba(15,23,42,0.08)",
                        minWidth: 180,
                      })}
                    >
                      {columns[0] ?? "Sous-Famille"}
                    </th>

                    {dataHeaders.map((h) => (
                      <th
                        key={h}
                        style={thS({
                          background: NW.indigo,
                          color: "#FFFFFF",
                          fontFamily: FONTS.heading,
                          fontSize: 11,
                          borderBottom: `2px solid ${NW.indigoDark}`,
                        })}
                      >
                        {h}
                      </th>
                    ))}

                    {hasCoutTTC && (
                      <th
                        style={thS({
                          background: NW.teal,
                          color: "#FFFFFF",
                          fontFamily: FONTS.heading,
                          fontSize: 11,
                          borderLeft: `3px solid rgba(255,255,255,0.25)`,
                          borderBottom: `2px solid rgba(15,23,42,0.15)`,
                        })}
                      >
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 2,
                          }}
                        >
                          <span style={{ fontSize: 14 }}>💎</span>
                          <span>Coût TTC</span>
                          <span
                            style={{
                              fontSize: 9,
                              opacity: 0.7,
                              fontWeight: 400,
                            }}
                          ></span>
                        </div>
                      </th>
                    )}

                    {hasMarge && (
                      <th
                        style={thS({
                          background: NW.orange,
                          color: "#FFFFFF",
                          fontFamily: FONTS.heading,
                          fontSize: 11,
                          borderLeft: `3px solid rgba(255,255,255,0.25)`,
                          borderBottom: `2px solid rgba(15,23,42,0.15)`,
                        })}
                      >
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 2,
                          }}
                        >
                          <span style={{ fontSize: 14 }}>📈</span>
                          <span>Bénéfice</span>
                          <span
                            style={{
                              fontSize: 9,
                              opacity: 0.7,
                              fontWeight: 400,
                            }}
                          ></span>
                        </div>
                      </th>
                    )}
                  </tr>
                </thead>

                <tbody>
                  {displayRows.map((row, i) => {
                    const rowBg = i % 2 === 0 ? NW.white : NW.gray50;
                    const coutTTC = hasCoutTTC ? getCoutTTC(row) : null;
                    const marge = hasMarge ? getMarge(row) : null;
                    const margePos = marge !== null && marge >= 0;
                    const chipColor = CHIP_PALETTE[i % CHIP_PALETTE.length];

                    return (
                      <tr
                        key={i}
                        className="nw-row-hover"
                        style={{
                          background: rowBg,
                          cursor: "pointer",
                          transition: "background 0.12s",
                        }}
                        onClick={() =>
                          setDrilldownSousFamille(String(row[0] ?? ""))
                        }
                        title={`Voir le détail par dépôt pour "${String(row[0] ?? "")}"`}
                      >
                        <td
                          style={tdS({
                            fontWeight: 700,
                            color: NW.indigoDark,
                            background:
                              i % 2 === 0 ? NW.indigoLight : "#D7E5F5",
                            borderRight: `3px solid ${NW.indigo}`,
                            position: "sticky",
                            left: 0,
                            zIndex: 2,
                            boxShadow: "2px 0 6px rgba(29,91,168,0.06)",
                          })}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            <span
                              style={{
                                width: 9,
                                height: 9,
                                borderRadius: "50%",
                                background: chipColor,
                                flexShrink: 0,
                                display: "inline-block",
                                boxShadow: `0 0 5px ${chipColor}66`,
                              }}
                            />
                            {String(row[0] ?? "")}
                          </div>
                        </td>

                        {dataHeaders.map((h, di) => (
                          <td
                            key={di}
                            style={tdS({
                              textAlign: "right",
                              color: NW.gray800,
                              fontVariantNumeric: "tabular-nums",
                            })}
                          >
                            {fmtNum(applyRule(row[1 + di], negateFlags[di]))}
                          </td>
                        ))}

                        {hasCoutTTC && coutTTC !== null && (
                          <td
                            style={tdS({
                              textAlign: "right",
                              color: NW.teal,
                              fontWeight: 700,
                              background:
                                i % 2 === 0
                                  ? "rgba(0,160,140,0.06)"
                                  : "rgba(0,160,140,0.10)",
                              borderLeft: `3px solid ${NW.teal}44`,
                              fontVariantNumeric: "tabular-nums",
                            })}
                          >
                            {fmtNum(coutTTC)}
                          </td>
                        )}

                        {hasMarge && marge !== null && (
                          <td
                            style={tdS({
                              textAlign: "right",
                              fontWeight: 700,
                              color: margePos ? NW.success : NW.danger,
                              background: margePos
                                ? "rgba(0,160,140,0.07)"
                                : "rgba(214,69,69,0.07)",
                              borderLeft: `3px solid ${margePos ? NW.success : NW.danger}44`,
                              fontVariantNumeric: "tabular-nums",
                            })}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-end",
                                gap: 5,
                              }}
                            >
                              <span
                                style={{
                                  fontSize: 10,
                                  animation: margePos
                                    ? "nwPulseGreen 2s infinite"
                                    : "nwPulseRed 2s infinite",
                                  borderRadius: "50%",
                                  width: 8,
                                  height: 8,
                                  background: margePos ? NW.success : NW.danger,
                                  display: "inline-block",
                                  flexShrink: 0,
                                }}
                              />
                              {margePos ? "" : "−"}
                              {fmtNum(Math.abs(marge))}
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>

                {/* Grand total footer */}
                {displayRows.length > 1 && (
                  <tfoot style={{ position: "sticky", bottom: 0, zIndex: 5 }}>
                    <tr>
                      <td
                        style={tdS({
                          background: NW.indigoDark,
                          color: "#FFFFFF",
                          fontWeight: 800,
                          fontSize: 12,
                          fontFamily: FONTS.heading,
                          borderRight: `3px solid ${NW.rose}`,
                          position: "sticky",
                          left: 0,
                          zIndex: 10,
                        })}
                      >
                        ∑ TOTAL GÉNÉRAL
                      </td>
                      {grandTotals.map((val, i) => (
                        <td
                          key={i}
                          style={tdS({
                            background: NW.indigoDark,
                            color: NW.indigoMed,
                            fontWeight: 700,
                            textAlign: "right",
                            fontVariantNumeric: "tabular-nums",
                          })}
                        >
                          {fmtNum(val)}
                        </td>
                      ))}
                      {hasCoutTTC && totalCoutTTC !== null && (
                        <td
                          style={tdS({
                            background: NW.teal,
                            color: "#FFFFFF",
                            fontWeight: 800,
                            textAlign: "right",
                            borderLeft: `3px solid rgba(255,255,255,0.2)`,
                            fontVariantNumeric: "tabular-nums",
                          })}
                        >
                          {fmtNum(totalCoutTTC)}
                        </td>
                      )}
                      {hasMarge && totalMarge !== null && (
                        <td
                          style={tdS({
                            background:
                              totalMarge >= 0 ? NW.success : NW.danger,
                            color: "#FFFFFF",
                            fontWeight: 800,
                            textAlign: "right",
                            borderLeft: `3px solid rgba(255,255,255,0.2)`,
                            fontVariantNumeric: "tabular-nums",
                          })}
                        >
                          {totalMarge >= 0 ? "+" : "−"}
                          {fmtNum(Math.abs(totalMarge))}
                        </td>
                      )}
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}

          {/* ── Dashboard view ────────────────────────────────────────── */}
          {viewMode === "dashboard" && (
            <div
              style={{
                padding: 20,
                animation: "nwFadeSlideIn 0.42s cubic-bezier(0.22,1,0.36,1)",
              }}
            >
              <PeriodDashboard
                data={dashboardData}
                rowDimCount={1}
                topN={topN}
              />
            </div>
          )}

          {/* ── Comparaison view ──────────────────────────────────────── */}
          {viewMode === "comparaison" && (
            <div
              style={{
                padding: 20,
                animation: "nwFadeSlideIn 0.42s cubic-bezier(0.22,1,0.36,1)",
              }}
            >
              <ComparisonView
                defaultMeasures={selMeasures}
                defaultDimension={DIM_NAME}
                defaultHierarchy={HIER_NAME}
                defaultLevel={LEVEL_NAME}
                overrideTopN={topN}
                saveType="sousfamille"
                initialComparisonPeriods={restoredComparisonPeriods}
                autoRunToken={comparisonAutoRunToken}
              />
            </div>
          )}

          {/* ── Évolution view ────────────────────────────────────────── */}
          {viewMode === "evolution" && (
            <div
              style={{
                padding: 20,
                animation: "nwFadeSlideIn 0.42s cubic-bezier(0.22,1,0.36,1)",
              }}
            >
              <EvolutionView
                key={evolutionKey}
                defaultMeasure={selMeasures[0] ?? ""}
                defaultDimension={DIM_NAME}
                defaultHierarchy={HIER_NAME}
                defaultLevel={LEVEL_NAME}
                overrideMeasures={selMeasures}
                overrideDateFrom={dateFrom || undefined}
                overrideDateTo={dateTo || undefined}
                overrideDepotFilter={
                  selMembers !== null ? Array.from(selMembers) : undefined
                }
              />
            </div>
          )}
        </div>
      )}

      {/* ══ EMPTY STATE ═══════════════════════════════════════════════════ */}
      {allRows.length === 0 && !loading && !error && (
        <div
          style={{
            color: NW.gray500,
            padding: 60,
            textAlign: "center",
            fontSize: 14,
            background: NW.white,
            borderRadius: 16,
            border: `1.5px dashed ${NW.indigoMed}`,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 12,
          }}
        >
          <span style={{ fontSize: 48 }}>🗂️</span>
          <div
            style={{
              fontWeight: 800,
              color: NW.indigo,
              fontSize: 16,
              fontFamily: FONTS.heading,
            }}
          >
            Aucune donnée affichée
          </div>
          <div style={{ fontSize: 13, color: NW.gray500, lineHeight: 1.7 }}>
            Sélectionnez des mesures puis cliquez{" "}
            <strong style={{ color: NW.indigo }}>▶ Calculer</strong>
            <br />
            Pour voir les colonnes <strong>Coût TTC</strong> et{" "}
            <strong>Marge</strong>, sélectionnez{" "}
            <span
              style={{
                background: NW.indigoLight,
                color: NW.indigoDark,
                borderRadius: 5,
                padding: "1px 6px",
                fontWeight: 700,
              }}
            >
              PUHT Achat + TVA Achat + PTTC
            </span>
          </div>
        </div>
      )}

      {/* ══ FILENAME EXPORT MODAL ════════════════════════════════════════ */}
      {showFilenameModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 99999,
            background: "rgba(13,42,80,0.45)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            animation: "nwFadeSlideIn 0.18s ease",
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowFilenameModal(false);
          }}
        >
          <div
            style={{
              background: NW.white,
              borderRadius: 18,
              boxShadow: "0 24px 80px rgba(29,91,168,0.28)",
              width: 440,
              overflow: "hidden",
              border: `1.5px solid ${NW.teal}33`,
            }}
          >
            <div
              style={{
                background:
                  pendingExportType === "pdf"
                    ? `linear-gradient(135deg, ${NW.violetDark}, ${NW.violet})`
                    : `linear-gradient(135deg, ${NW.teal}, #00806F)`,
                padding: "18px 22px",
                display: "flex",
                alignItems: "center",
                gap: 12,
              }}
            >
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 11,
                  background: "rgba(255,255,255,0.15)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 20,
                  flexShrink: 0,
                }}
              >
                {pendingExportType === "pdf" ? "📄" : "📊"}
              </div>
              <div>
                <div
                  style={{
                    fontFamily: FONTS.heading,
                    fontWeight: 800,
                    fontSize: 15,
                    color: "#FFFFFF",
                  }}
                >
                  Exporter en {pendingExportType === "pdf" ? "PDF" : "Excel"}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: "rgba(255,255,255,0.65)",
                    marginTop: 2,
                  }}
                >
                  {pendingExportType === "pdf"
                    ? "Tableau · Dashboard · Comparaison · Évolution"
                    : "Tableau complet + feuille Résumé"}
                </div>
              </div>
              <button
                onClick={() => setShowFilenameModal(false)}
                style={{
                  marginLeft: "auto",
                  background: "rgba(255,255,255,0.15)",
                  border: "none",
                  borderRadius: 8,
                  color: "#FFFFFF",
                  fontSize: 16,
                  width: 30,
                  height: 30,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                }}
              >
                ✕
              </button>
            </div>
            <div style={{ padding: "24px 22px" }}>
              <label
                style={{
                  display: "block",
                  fontWeight: 700,
                  fontSize: 12,
                  color: NW.gray700,
                  marginBottom: 8,
                  fontFamily: FONTS.body,
                  letterSpacing: 0.2,
                }}
              >
                📝 Nom du fichier{" "}
                <span style={{ color: NW.danger, fontWeight: 800 }}>*</span>
              </label>
              <div style={{ display: "flex", alignItems: "center" }}>
                <input
                  type="text"
                  value={exportFilename}
                  onChange={(e) => setExportFilename(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleConfirmExport();
                    if (e.key === "Escape") setShowFilenameModal(false);
                  }}
                  autoFocus
                  placeholder={`SousFamille_${new Date().toISOString().slice(0, 10)}`}
                  style={{
                    flex: 1,
                    border: `1.5px solid ${NW.violetMed}`,
                    borderRight: "none",
                    borderRadius: "10px 0 0 10px",
                    padding: "10px 14px",
                    fontSize: 13,
                    fontFamily: FONTS.body,
                    color: NW.gray800,
                    outline: "none",
                    background: NW.gray50,
                  }}
                />
                <div
                  style={{
                    padding: "10px 13px",
                    border: `1.5px solid ${NW.violetMed}`,
                    borderLeft: "none",
                    borderRadius: "0 10px 10px 0",
                    background: NW.gray100,
                    fontSize: 12,
                    color: NW.gray500,
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                  }}
                >
                  .{pendingExportType === "pdf" ? "pdf" : "xlsx"}
                </div>
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: NW.gray500,
                  marginTop: 8,
                  fontStyle: "italic",
                }}
              >
                Laissez vide pour utiliser le nom par défaut
              </div>
              <div
                style={{
                  marginTop: 16,
                  padding: "10px 14px",
                  borderRadius: 10,
                  background: NW.violetSoft,
                  border: `1px solid ${NW.violetMed}`,
                  fontSize: 11,
                  color: NW.violet,
                  fontWeight: 600,
                  display: "flex",
                  gap: 8,
                }}
              >
                <span style={{ fontSize: 14, flexShrink: 0 }}>
                  {pendingExportType === "pdf" ? "📋" : "📊"}
                </span>
                <div>
                  {pendingExportType === "pdf"
                    ? "PDF paysage A4 — Page de couverture · Tableau · Dashboard · Comparaison · Évolution"
                    : "Classeur Excel — Feuille « Tableau » + feuille « Résumé »"}
                  {topN && (
                    <span style={{ display: "block", marginTop: 4 }}>
                      Top {topN} · {displayRows.length} lignes exportées
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div
              style={{
                padding: "14px 22px",
                borderTop: `1px solid ${NW.gray100}`,
                display: "flex",
                gap: 10,
                justifyContent: "flex-end",
                background: NW.gray50,
              }}
            >
              <button
                onClick={() => setShowFilenameModal(false)}
                style={{
                  padding: "9px 20px",
                  border: `1.5px solid ${NW.gray200}`,
                  borderRadius: 10,
                  background: NW.white,
                  color: NW.gray700,
                  fontWeight: 600,
                  fontSize: 12,
                  fontFamily: FONTS.body,
                  cursor: "pointer",
                }}
              >
                Annuler
              </button>
              <button
                onClick={handleConfirmExport}
                style={{
                  padding: "9px 22px",
                  border: "none",
                  borderRadius: 10,
                  background:
                    pendingExportType === "pdf"
                      ? `linear-gradient(135deg, ${NW.violetDark}, ${NW.violet})`
                      : `linear-gradient(135deg, ${NW.teal}, #00806F)`,
                  color: "#FFFFFF",
                  fontWeight: 700,
                  fontSize: 12,
                  fontFamily: FONTS.body,
                  cursor: "pointer",
                  boxShadow: "0 4px 14px rgba(0,160,140,0.30)",
                  display: "flex",
                  alignItems: "center",
                  gap: 7,
                }}
              >
                {pendingExportType === "pdf" ? "📄" : "📊"} Exporter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══ MEASURE PICKER MODAL — VENTE ══════════════════════════════════ */}
      {showMeasurePickerVente &&
        (() => {
          const grp = measureGroupDefs.find((g) => g.label === "Vente")!;
          return (
            <div
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(15,23,42,0.52)",
                zIndex: 10000,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backdropFilter: "blur(3px)",
                animation: "nwFadeSlideIn 0.18s ease",
              }}
              onClick={() => setShowMeasurePickerVente(false)}
            >
              <div
                style={{
                  background: NW.white,
                  borderRadius: 18,
                  boxShadow: "0 24px 80px rgba(127,200,189,0.22)",
                  width: "min(640px, 95vw)",
                  maxHeight: "78vh",
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div
                  style={{
                    background: `linear-gradient(135deg, ${NW.roseDark}, ${NW.rose})`,
                    padding: "16px 22px",
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                  }}
                >
                  <span style={{ fontSize: 22 }}>🛒</span>
                  <div>
                    <div
                      style={{
                        fontFamily: FONTS.heading,
                        fontWeight: 800,
                        fontSize: 15,
                        color: "#FFFFFF",
                      }}
                    >
                      Mesures Vente
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: "rgba(255,255,255,0.65)",
                        marginTop: 2,
                      }}
                    >
                      {
                        selMeasures.filter((n) =>
                          grp.items.some((m) => m.name === n),
                        ).length
                      }{" "}
                      sélectionnée(s)
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    padding: "10px 20px",
                    borderBottom: `1px solid ${NW.gray100}`,
                  }}
                >
                  <input
                    autoFocus
                    type="text"
                    placeholder="🔍 Rechercher une mesure…"
                    value={pickerSearch}
                    onChange={(e) => setPickerSearch(e.target.value)}
                    style={{
                      width: "100%",
                      border: `1.5px solid ${NW.roseMed}`,
                      borderRadius: 9,
                      padding: "7px 12px",
                      fontSize: 13,
                      fontFamily: FONTS.body,
                      color: NW.gray800,
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                  />
                </div>
                <div
                  style={{
                    overflowY: "auto",
                    flex: 1,
                    padding: "14px 20px 20px",
                  }}
                >
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {(() => {
                      const pinnedNames = new Set(
                        (
                          pinnedGroupDefs.find((g) => g.label === "Vente")
                            ?.items ?? []
                        ).map((p) => p.name),
                      );
                      return grp.items
                        .filter((m) => !pinnedNames.has(m.name))
                        .filter(
                          (m) =>
                            !pickerSearch ||
                            m.caption
                              .toLowerCase()
                              .includes(pickerSearch.toLowerCase()) ||
                            m.name
                              .toLowerCase()
                              .includes(pickerSearch.toLowerCase()),
                        )
                        .map((m) => {
                          const checked = selMeasures.includes(m.name);
                          return (
                            <label
                              key={m.name}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                fontSize: 12,
                                cursor: "pointer",
                                background: checked ? NW.roseLight : NW.gray50,
                                border: `1.5px solid ${checked ? NW.rose : NW.gray200}`,
                                borderRadius: 9,
                                padding: "5px 12px",
                                color: checked ? NW.rose : NW.gray700,
                                fontWeight: checked ? 700 : 400,
                                transition: "all 0.12s",
                                boxShadow: checked
                                  ? `0 0 0 2px ${NW.rose}22`
                                  : undefined,
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleMeasure(m.name)}
                                style={{ accentColor: NW.rose, margin: 0 }}
                              />
                              {m.caption}
                            </label>
                          );
                        });
                    })()}
                  </div>
                </div>
                <div
                  style={{
                    padding: "12px 20px",
                    borderTop: `1px solid ${NW.gray100}`,
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 8,
                    background: NW.gray50,
                  }}
                >
                  <button
                    onClick={() => {
                      setSelMeasures(popupInitialMeasures);
                      setShowMeasurePickerVente(false);
                    }}
                    style={{
                      padding: "9px 20px",
                      borderRadius: 10,
                      border: `1.5px solid ${NW.danger}`,
                      background: NW.white,
                      color: NW.danger,
                      fontWeight: 700,
                      fontSize: 13,
                      fontFamily: FONTS.body,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                    }}
                  >
                    ✕ Annuler
                  </button>
                  <button
                    onClick={() => setShowMeasurePickerVente(false)}
                    style={{
                      padding: "9px 24px",
                      border: "none",
                      borderRadius: 10,
                      background: `linear-gradient(135deg, ${NW.roseDark}, ${NW.rose})`,
                      color: "#FFFFFF",
                      fontWeight: 700,
                      fontSize: 13,
                      fontFamily: FONTS.body,
                      cursor: "pointer",
                      boxShadow: `0 4px 16px rgba(127,200,189,0.30)`,
                    }}
                  >
                    ✓ Valider
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

      {/* ══ MEASURE PICKER MODAL — ACHAT ══════════════════════════════════ */}
      {showMeasurePickerAchat &&
        (() => {
          const grp = measureGroupDefs.find((g) => g.label === "Achat")!;
          return (
            <div
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(15,23,42,0.52)",
                zIndex: 10000,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backdropFilter: "blur(3px)",
                animation: "nwFadeSlideIn 0.18s ease",
              }}
              onClick={() => setShowMeasurePickerAchat(false)}
            >
              <div
                style={{
                  background: NW.white,
                  borderRadius: 18,
                  boxShadow: "0 24px 80px rgba(29,91,168,0.22)",
                  width: "min(640px, 95vw)",
                  maxHeight: "78vh",
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div
                  style={{
                    background: `linear-gradient(135deg, ${NW.violetDark}, ${NW.violet})`,
                    padding: "16px 22px",
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                  }}
                >
                  <span style={{ fontSize: 22 }}>📦</span>
                  <div>
                    <div
                      style={{
                        fontFamily: FONTS.heading,
                        fontWeight: 800,
                        fontSize: 15,
                        color: "#FFFFFF",
                      }}
                    >
                      Mesures Achat
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: "rgba(255,255,255,0.65)",
                        marginTop: 2,
                      }}
                    >
                      {
                        selMeasures.filter((n) =>
                          grp.items.some((m) => m.name === n),
                        ).length
                      }{" "}
                      sélectionnée(s)
                    </div>
                  </div>
                  <button
                    onClick={() => setShowMeasurePickerAchat(false)}
                    style={{
                      marginLeft: "auto",
                      background: "rgba(255,255,255,0.18)",
                      border: "none",
                      borderRadius: 8,
                      color: "#FFFFFF",
                      fontSize: 16,
                      width: 30,
                      height: 30,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 700,
                    }}
                  >
                    ✕
                  </button>
                </div>
                <div
                  style={{
                    padding: "10px 20px",
                    borderBottom: `1px solid ${NW.gray100}`,
                  }}
                >
                  <input
                    autoFocus
                    type="text"
                    placeholder="🔍 Rechercher une mesure…"
                    value={pickerSearch}
                    onChange={(e) => setPickerSearch(e.target.value)}
                    style={{
                      width: "100%",
                      border: `1.5px solid ${NW.violetMed}`,
                      borderRadius: 9,
                      padding: "7px 12px",
                      fontSize: 13,
                      fontFamily: FONTS.body,
                      color: NW.gray800,
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                  />
                </div>
                <div
                  style={{
                    overflowY: "auto",
                    flex: 1,
                    padding: "14px 20px 20px",
                  }}
                >
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {(() => {
                      const pinnedNames = new Set(
                        (
                          pinnedGroupDefs.find((g) => g.label === "Achat")
                            ?.items ?? []
                        ).map((p) => p.name),
                      );
                      return grp.items
                        .filter((m) => !pinnedNames.has(m.name))
                        .filter(
                          (m) =>
                            !pickerSearch ||
                            m.caption
                              .toLowerCase()
                              .includes(pickerSearch.toLowerCase()) ||
                            m.name
                              .toLowerCase()
                              .includes(pickerSearch.toLowerCase()),
                        )
                        .map((m) => {
                          const checked = selMeasures.includes(m.name);
                          return (
                            <label
                              key={m.name}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                fontSize: 12,
                                cursor: "pointer",
                                background: checked
                                  ? NW.violetLight
                                  : NW.gray50,
                                border: `1.5px solid ${checked ? NW.violet : NW.gray200}`,
                                borderRadius: 9,
                                padding: "5px 12px",
                                color: checked ? NW.violet : NW.gray700,
                                fontWeight: checked ? 700 : 400,
                                transition: "all 0.12s",
                                boxShadow: checked
                                  ? `0 0 0 2px ${NW.violet}22`
                                  : undefined,
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleMeasure(m.name)}
                                style={{ accentColor: NW.violet, margin: 0 }}
                              />
                              {m.caption}
                            </label>
                          );
                        });
                    })()}
                  </div>
                </div>
                <div
                  style={{
                    padding: "12px 20px",
                    borderTop: `1px solid ${NW.gray100}`,
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 8,
                    background: NW.gray50,
                  }}
                >
                  <button
                    onClick={() => {
                      setSelMeasures(popupInitialMeasures);
                      setShowMeasurePickerAchat(false);
                    }}
                    style={{
                      padding: "9px 20px",
                      borderRadius: 10,
                      border: `1.5px solid ${NW.danger}`,
                      background: NW.white,
                      color: NW.danger,
                      fontWeight: 700,
                      fontSize: 13,
                      fontFamily: FONTS.body,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                    }}
                  >
                    ✕ Annuler
                  </button>
                  <button
                    onClick={() => setShowMeasurePickerAchat(false)}
                    style={{
                      padding: "9px 24px",
                      border: "none",
                      borderRadius: 10,
                      background: `linear-gradient(135deg, ${NW.violetDark}, ${NW.violet})`,
                      color: "#FFFFFF",
                      fontWeight: 700,
                      fontSize: 13,
                      fontFamily: FONTS.body,
                      cursor: "pointer",
                      boxShadow: `0 4px 16px rgba(29,91,168,0.30)`,
                    }}
                  >
                    ✓ Valider
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

      {/* ══ DRILLDOWN MODAL ══════════════════════════════════════════════ */}
      {drilldownResult && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 99998,
            background: "rgba(13,42,80,0.55)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            animation: "nwFadeSlideIn 0.22s ease",
          }}
          onClick={() => setDrilldownSousFamille(null)}
        >
          <div
            style={{
              background: NW.white,
              borderRadius: 18,
              boxShadow: "0 24px 80px rgba(29,91,168,0.30)",
              width: "min(96vw, 1100px)",
              maxHeight: "85vh",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              border: `1.5px solid ${NW.violetMed}`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div
              style={{
                background: `linear-gradient(135deg, ${NW.violetDark}, ${NW.violet})`,
                padding: "16px 22px",
                display: "flex",
                alignItems: "center",
                gap: 12,
                flexShrink: 0,
              }}
            >
              <span style={{ fontSize: 24 }}>🏪</span>
              <div>
                <div
                  style={{
                    fontFamily: FONTS.heading,
                    fontWeight: 800,
                    fontSize: 16,
                    color: "#FFFFFF",
                  }}
                >
                  Détail par Dépôt
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: "rgba(255,255,255,0.65)",
                    marginTop: 2,
                  }}
                >
                  Sous-Famille :{" "}
                  <strong style={{ color: NW.roseMed }}>
                    {drilldownResult.sousFamille}
                  </strong>
                  {" · "}
                  {drilldownResult.rows.length} dépôt
                  {drilldownResult.rows.length !== 1 ? "s" : ""}
                </div>
              </div>
              <button
                onClick={() => setDrilldownSousFamille(null)}
                style={{
                  marginLeft: "auto",
                  background: "rgba(255,255,255,0.18)",
                  border: "none",
                  borderRadius: 8,
                  color: "#FFFFFF",
                  fontSize: 18,
                  width: 32,
                  height: 32,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                }}
              >
                ✕
              </button>
            </div>

            {/* Table */}
            <div style={{ overflowX: "auto", overflowY: "auto", flex: 1 }}>
              {(() => {
                const dCols = drilldownResult.cols;
                const dRows = drilldownResult.rows;
                const dDataHeaders = dCols.slice(1);
                const dPuhtIdx = dDataHeaders.findIndex((h) => /puht/i.test(h));
                const dTvaIdx = dDataHeaders.findIndex((h) =>
                  /\btva\b/i.test(h),
                );
                const dPttcIdx = dDataHeaders.findIndex((h) => /pttc/i.test(h));
                const dQteVenteIdx = dDataHeaders.findIndex(
                  (h) => /qte/i.test(h) && !/achat/i.test(h),
                );
                const dHasCout =
                  !hasCoutTTCSelected &&
                  dPuhtIdx >= 0 &&
                  dTvaIdx >= 0 &&
                  dQteVenteIdx >= 0;
                const dHasMarge =
                  !hasBenificeSelected && dHasCout && dPttcIdx >= 0;
                const getV = (row: any[], idx: number) => {
                  const v = row[1 + idx];
                  const n =
                    typeof v === "number" ? v : parseFloat(String(v ?? "0"));
                  const val = isNaN(n) ? 0 : n;
                  return applyRule(
                    val,
                    colHeaderShouldNegate(dDataHeaders[idx], cubeMeasures),
                  ) as number;
                };
                const getCout = (row: any[]) =>
                  getV(row, dQteVenteIdx) *
                  (getV(row, dPuhtIdx) * ((100 + getV(row, dTvaIdx)) / 100));
                const getMarg = (row: any[]) =>
                  getV(row, dPttcIdx) - getCout(row);
                const totals = dDataHeaders.map((_, i) =>
                  dRows.reduce((s, r) => s + getV(r, i), 0),
                );
                const totalCout = dHasCout
                  ? dRows.reduce((s, r) => s + getCout(r), 0)
                  : null;
                const totalMarg = dHasMarge
                  ? dRows.reduce((s, r) => s + getMarg(r), 0)
                  : null;
                return (
                  <table
                    style={{
                      borderCollapse: "collapse",
                      fontSize: 12,
                      fontFamily: FONTS.body,
                      width: "100%",
                      minWidth: 500,
                    }}
                  >
                    <thead style={{ position: "sticky", top: 0, zIndex: 5 }}>
                      <tr>
                        <th
                          style={thS({
                            background: NW.indigoDark,
                            color: "#FFFFFF",
                            textAlign: "left",
                            fontFamily: FONTS.heading,
                            fontSize: 12,
                            minWidth: 140,
                            position: "sticky",
                            left: 0,
                            zIndex: 10,
                            borderRight: `3px solid ${NW.indigo}`,
                          })}
                        >
                          Dépôt
                        </th>
                        {dDataHeaders.map((h) => (
                          <th
                            key={h}
                            style={thS({
                              background: NW.rose,
                              color: "#FFFFFF",
                              fontSize: 11,
                            })}
                          >
                            {h}
                          </th>
                        ))}
                        {dHasCout && (
                          <th
                            style={thS({
                              background: NW.teal,
                              color: "#FFFFFF",
                              fontSize: 11,
                              borderLeft: "3px solid rgba(255,255,255,0.25)",
                            })}
                          >
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 2,
                              }}
                            >
                              <span>💎 Coût TTC</span>
                              <span
                                style={{
                                  fontSize: 9,
                                  opacity: 0.7,
                                  fontWeight: 400,
                                }}
                              >
                                Qté × PUHT × (1+TVA%)
                              </span>
                            </div>
                          </th>
                        )}
                        {dHasMarge && (
                          <th
                            style={thS({
                              background: NW.orange,
                              color: "#FFFFFF",
                              fontSize: 11,
                              borderLeft: "3px solid rgba(255,255,255,0.25)",
                            })}
                          >
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 2,
                              }}
                            >
                              <span>📈 Bénéfice</span>
                              <span
                                style={{
                                  fontSize: 9,
                                  opacity: 0.7,
                                  fontWeight: 400,
                                }}
                              >
                                PTTC − Coût TTC
                              </span>
                            </div>
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {dRows.map((row, i) => {
                        const cout = dHasCout ? getCout(row) : null;
                        const marg = dHasMarge ? getMarg(row) : null;
                        const margePos = marg !== null && marg >= 0;
                        const chip = CHIP_PALETTE[i % CHIP_PALETTE.length];
                        return (
                          <tr
                            key={i}
                            className="nw-row-hover"
                            style={{
                              background: i % 2 === 0 ? NW.white : NW.gray50,
                            }}
                          >
                            <td
                              style={tdS({
                                fontWeight: 700,
                                color: NW.indigoDark,
                                background:
                                  i % 2 === 0 ? NW.indigoLight : "#D7E5F5",
                                borderRight: `3px solid ${NW.indigo}`,
                                position: "sticky",
                                left: 0,
                                zIndex: 2,
                              })}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 7,
                                }}
                              >
                                <span
                                  style={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: "50%",
                                    background: chip,
                                    flexShrink: 0,
                                  }}
                                />
                                {String(row[0] ?? "")}
                              </div>
                            </td>
                            {dDataHeaders.map((_, di) => (
                              <td
                                key={di}
                                style={tdS({
                                  textAlign: "right",
                                  fontVariantNumeric: "tabular-nums",
                                })}
                              >
                                {fmtNum(getV(row, di))}
                              </td>
                            ))}
                            {dHasCout && cout !== null && (
                              <td
                                style={tdS({
                                  textAlign: "right",
                                  color: NW.teal,
                                  fontWeight: 700,
                                  background:
                                    i % 2 === 0
                                      ? "rgba(0,160,140,0.06)"
                                      : "rgba(0,160,140,0.10)",
                                  borderLeft: `3px solid ${NW.teal}44`,
                                  fontVariantNumeric: "tabular-nums",
                                })}
                              >
                                {fmtNum(cout)}
                              </td>
                            )}
                            {dHasMarge && marg !== null && (
                              <td
                                style={tdS({
                                  textAlign: "right",
                                  fontWeight: 700,
                                  color: margePos ? NW.success : NW.danger,
                                  background: margePos
                                    ? "rgba(0,160,140,0.07)"
                                    : "rgba(214,69,69,0.07)",
                                  borderLeft: `3px solid ${margePos ? NW.success : NW.danger}44`,
                                  fontVariantNumeric: "tabular-nums",
                                })}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "flex-end",
                                    gap: 5,
                                  }}
                                >
                                  <span
                                    style={{
                                      width: 7,
                                      height: 7,
                                      borderRadius: "50%",
                                      background: margePos
                                        ? NW.success
                                        : NW.danger,
                                      flexShrink: 0,
                                    }}
                                  />
                                  {margePos ? "" : "−"}
                                  {fmtNum(Math.abs(marg))}
                                </div>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                    {dRows.length > 1 && (
                      <tfoot
                        style={{ position: "sticky", bottom: 0, zIndex: 5 }}
                      >
                        <tr>
                          <td
                            style={tdS({
                              background: NW.indigoDark,
                              color: "#FFFFFF",
                              fontWeight: 800,
                              fontSize: 12,
                              fontFamily: FONTS.heading,
                              position: "sticky",
                              left: 0,
                              zIndex: 10,
                              borderRight: `3px solid ${NW.rose}`,
                            })}
                          >
                            ∑ TOTAL GÉNÉRAL
                          </td>
                          {totals.map((val, i) => (
                            <td
                              key={i}
                              style={tdS({
                                background: NW.indigoDark,
                                color: NW.indigoMed,
                                fontWeight: 700,
                                textAlign: "right",
                                fontVariantNumeric: "tabular-nums",
                              })}
                            >
                              {fmtNum(val)}
                            </td>
                          ))}
                          {dHasCout && totalCout !== null && (
                            <td
                              style={tdS({
                                background: NW.teal,
                                color: "#FFFFFF",
                                fontWeight: 800,
                                textAlign: "right",
                                borderLeft: "3px solid rgba(255,255,255,0.2)",
                                fontVariantNumeric: "tabular-nums",
                              })}
                            >
                              {fmtNum(totalCout)}
                            </td>
                          )}
                          {dHasMarge && totalMarg !== null && (
                            <td
                              style={tdS({
                                background:
                                  totalMarg >= 0 ? NW.success : NW.danger,
                                color: "#FFFFFF",
                                fontWeight: 800,
                                textAlign: "right",
                                borderLeft: "3px solid rgba(255,255,255,0.2)",
                                fontVariantNumeric: "tabular-nums",
                              })}
                            >
                              {totalMarg >= 0 ? "" : "−"}
                              {fmtNum(Math.abs(totalMarg))}
                            </td>
                          )}
                        </tr>
                      </tfoot>
                    )}
                  </table>
                );
              })()}
            </div>

            {/* Footer */}
            <div
              style={{
                padding: "12px 20px",
                borderTop: `1px solid ${NW.gray100}`,
                background: NW.gray50,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexShrink: 0,
              }}
            >
              <span
                style={{ fontSize: 11, color: NW.gray500, fontWeight: 600 }}
              >
                💡 Coût TTC = Qté Vente × (PUHT Achat × (100 + TVA Achat) /
                100). Marge = PTTC − Coût TTC
              </span>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <div style={{ position: "relative" }}>
                  <button
                    onClick={() => setDrillExportOpen((v) => !v)}
                    style={{
                      padding: "8px 16px",
                      border: "none",
                      borderRadius: 10,
                      background: `linear-gradient(135deg, ${NW.teal}, #00806F)`,
                      color: "#FFFFFF",
                      fontWeight: 700,
                      fontSize: 13,
                      fontFamily: FONTS.body,
                      cursor: "pointer",
                      boxShadow: `0 4px 14px rgba(0,160,140,0.30)`,
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    📤 Exporter
                    <span
                      style={{
                        fontSize: 10,
                        opacity: 0.8,
                        display: "inline-block",
                        transform: drillExportOpen
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                        transition: "transform 0.15s",
                      }}
                    >
                      ▼
                    </span>
                  </button>
                  {drillExportOpen && (
                    <>
                      <div
                        onClick={() => setDrillExportOpen(false)}
                        style={{ position: "fixed", inset: 0, zIndex: 9994 }}
                      />
                      <div
                        style={{
                          position: "absolute",
                          bottom: "calc(100% + 6px)",
                          right: 0,
                          zIndex: 9995,
                          background: NW.white,
                          borderRadius: 12,
                          boxShadow: "0 10px 40px rgba(0,160,140,0.18)",
                          border: `1.5px solid ${NW.tealLight}`,
                          minWidth: 200,
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            background: `linear-gradient(135deg, ${NW.teal}, #00806F)`,
                            padding: "10px 14px",
                          }}
                        >
                          <div
                            style={{
                              fontFamily: FONTS.heading,
                              fontWeight: 800,
                              fontSize: 12,
                              color: "#FFFFFF",
                            }}
                          >
                            📤 Exporter le détail
                          </div>
                          <div
                            style={{
                              fontSize: 9,
                              color: "rgba(255,255,255,0.65)",
                              marginTop: 2,
                            }}
                          >
                            Choisissez le format
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            setDrillExportOpen(false);
                            setIsDrilldownExport(true);
                            setExportFilename(
                              `Détail_${drilldownSousFamille ?? "depot"}_${new Date().toISOString().slice(0, 10)}`,
                            );
                            setPendingExportType("excel");
                            setShowFilenameModal(true);
                          }}
                          style={{
                            width: "100%",
                            padding: "11px 16px",
                            border: "none",
                            borderBottom: `1px solid ${NW.gray100}`,
                            background: NW.white,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            fontFamily: FONTS.body,
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.background = NW.gray50)
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.background = NW.white)
                          }
                        >
                          <span style={{ fontSize: 20 }}>📊</span>
                          <div>
                            <div
                              style={{
                                fontWeight: 700,
                                fontSize: 12,
                                color: NW.gray800,
                              }}
                            >
                              Excel (.xlsx)
                            </div>
                            <div style={{ fontSize: 10, color: NW.gray500 }}>
                              Tableau détail par dépôt
                            </div>
                          </div>
                        </button>
                        <button
                          onClick={() => {
                            setDrillExportOpen(false);
                            setIsDrilldownExport(true);
                            setExportFilename(
                              `Détail_${drilldownSousFamille ?? "depot"}_${new Date().toISOString().slice(0, 10)}`,
                            );
                            setPendingExportType("pdf");
                            setShowFilenameModal(true);
                          }}
                          style={{
                            width: "100%",
                            padding: "11px 16px",
                            border: "none",
                            background: NW.white,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            fontFamily: FONTS.body,
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.background = NW.gray50)
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.background = NW.white)
                          }
                        >
                          <span style={{ fontSize: 20 }}>📄</span>
                          <div>
                            <div
                              style={{
                                fontWeight: 700,
                                fontSize: 12,
                                color: NW.gray800,
                              }}
                            >
                              PDF (.pdf)
                            </div>
                            <div style={{ fontSize: 10, color: NW.gray500 }}>
                              Tableau détail par dépôt
                            </div>
                          </div>
                        </button>
                      </div>
                    </>
                  )}
                </div>
                <button
                  onClick={() => setDrilldownSousFamille(null)}
                  style={{
                    padding: "8px 18px",
                    border: `1.5px solid ${NW.gray200}`,
                    borderRadius: 10,
                    background: NW.white,
                    color: NW.gray700,
                    fontWeight: 600,
                    fontSize: 13,
                    fontFamily: FONTS.body,
                    cursor: "pointer",
                  }}
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══ SAVE MODAL ════════════════════════════════════════════════════ */}
      {showSaveModal && (
        <SaveQueryModal
          onClose={() => setShowSaveModal(false)}
          onSaved={() => setSavedRefreshToken((t) => t + 1)}
          type="sousfamille"
          section={viewMode}
          overrideMeasures={selMeasures}
          overrideDateFrom={
            useDateFilter || soldeSelected != null ? dateFrom : null
          }
          overrideDateTo={
            useDateFilter || soldeSelected != null ? dateTo : null
          }
          overrideTopN={topN}
          overrideSoldeCodePeriode={soldeSelected?.code_periode ?? null}
          overrideDemarquePhase={selectedDemarquePhase}
        />
      )}
    </div>
  );
};

export default SousFamilleView;
